<template>
  <div class="section">
    <div class="container">
      <div class="level">
        <Header class="level-left" v-bind="$route.meta" />
      </div>
      <div ref="toolbar" class="forecast-toolbar" :class="{ 'is-condensed': isToolbarCondensed }">
        <b-field v-show="!isToolbarCondensed" class="toolbar-row" grouped>
          <b-field label="Start Date">
            <b-datepicker
              v-model="dateStart"
              name="dateStart"
              :first-day-of-week="1"
              :locale="'en-CA'"
              icon="calendar-alt"
              placeholder="Start Date"
            />
          </b-field>
          <b-field label="End Date">
            <b-datepicker
              v-model="dateEnd"
              name="dateEnd"
              :first-day-of-week="1"
              :locale="'en-CA'"
              icon="calendar-alt"
              placeholder="End Date"
            />
          </b-field>
          <b-field v-if="hasGroupPlants" label="Plant">
            <b-select v-model="selectedPlant" icon="industry" expanded>
              <option v-for="plant in groupPlants" :key="plant.id" :value="plant">
                {{ plant.name }}
              </option>
            </b-select>
          </b-field>
          <b-field v-else label="Plant">
            <multiselect
              v-model="selectedPlant"
              :options="plants"
              :multiple="false"
              :close-on-select="true"
              placeholder="Pick plant"
              :custom-label="(obj) => obj.id + ' - ' + obj.name"
              track-by="name"
              style="min-width: 400px;"
            />
          </b-field>
          <b-field v-if="forecasts.length > 0" label="Types">
            <multiselect
              v-model="selectedTypes"
              :options="siteTypes"
              :multiple="true"
              :close-on-select="false"
              placeholder="Pick Types to forecast"
              :custom-label="(obj) => obj.name"
              track-by="name"
              style="min-width: 400px;"
              @input="persistHiddenTypes"
            />
          </b-field>
        </b-field>
        <b-field v-show="!isToolbarCondensed" class="toolbar-row" grouped>
          <b-field>
            <b-button
              class="left-right"
              type="is-warning"
              icon-left="arrows-rotate"
              :disabled="isLoading || selectedPlant === null"
              @click="() => {resetData(); getData();}"
            >
              Load
            </b-button>
            <b-field label="Means" class="left-right ml-3">
              <b-dropdown :close-on-click="false" aria-role="list">
                <template #trigger="{}">
                  <b-button
                    :type="meansNeedReload ? 'is-warning' : 'is-light'"
                    icon-left="chart-line"
                    icon-right="angle-down"
                  >
                    {{ activeMeanCount }} of 4
                  </b-button>
                </template>
                <b-dropdown-item custom aria-role="listitem">
                  <b-checkbox v-model="collectMeanCounts">
                    Collect
                  </b-checkbox>
                </b-dropdown-item>
                <b-dropdown-item custom aria-role="listitem">
                  <b-checkbox v-model="washingMeanCounts">
                    Washing
                  </b-checkbox>
                </b-dropdown-item>
                <b-dropdown-item custom aria-role="listitem">
                  <b-checkbox v-model="repairMeanCounts">
                    Repair
                  </b-checkbox>
                </b-dropdown-item>
                <b-dropdown-item custom aria-role="listitem">
                  <b-checkbox v-model="rejectMeanCounts">
                    Reject
                  </b-checkbox>
                </b-dropdown-item>
                <!-- <b-dropdown-item custom aria-role="listitem">
                  <b-checkbox v-model="deliveryMeanCounts">
                    Delivery
                  </b-checkbox>
                </b-dropdown-item> -->
                <b-dropdown-item v-if="meansNeedReload" separator />
                <b-dropdown-item v-if="meansNeedReload" custom>
                  <span class="has-text-weight-semibold">Load again to apply</span>
                </b-dropdown-item>
              </b-dropdown>
            </b-field>
            <b-field v-if="selectedPlant && selectedPlant.id === 'FRA0'" label="Weekly Simorep Total" class="left-right ml-3">
              <b-input v-model="weeklyMeanSimorep" type="number" />
            </b-field>
            <b-tooltip :active="tooManyTypesForExcel" :label="excelDisabledReason" multilined>
              <b-button
                class="left-right ml-3"
                type="is-success"
                icon-left="download"
                :disabled="!forecasts.length > 0 || tooManyTypesForExcel"
                @click="() => {exportToExcel()}"
              >
                Export xlsx
              </b-button>
            </b-tooltip>
            <b-button
              class="left-right ml-3"
              type="is-success"
              icon-left="upload"
              :disabled="isLoading || !forecasts.length > 0"
              @click="() => {isImportingExcel = true}"
            >
              Import xlsx
            </b-button>
            <b-button
              class="left-right ml-3"
              type="is-warning"
              icon-left="x"
              :disabled="!forecasts.length > 0"
              @click="() => {isResetting = true}"
            >
              Reset
            </b-button>
            <div style="margin-left: 10px; margin-right: -10px; display: flex; flex-direction: column;">
              <b-switch v-model="shipmentView">
                Shipment View
              </b-switch>
            </div>
          </b-field>
          <b-field v-if="undecidedCollsOut.length > 0 || undecidedCollsIn.length > 0">
            <b-button
              class="left-right"
              type="is-success"
              icon-left="box"
              @click="() => {isCheckingUndecided = true}"
            >
              Undated Shipments
            </b-button>
          </b-field>
          <b-field v-if="forecasts.length > 0" label="Max Stocks" class="card px-2 py-2" grouped>
            <b-field>
              {{ "Max total: " + target.max_total }}
            </b-field>
            <b-field>
              {{ "Max Clean: " + target.max_clean }}
            </b-field>
          </b-field>
          <b-field v-if="forecasts.length > 0" label="Safety Stocks" class="card px-2 py-2" grouped>
            <b-field>
              {{ "Available: " + target.min_available }}
            </b-field>
            <b-field>
              {{ "Dirty: " + target.min_dirty }}
            </b-field>
            <b-field>
              {{ "Repair: " + target.min_repair }}
            </b-field>
            <b-field>
              {{ "Clean: " + target.min_clean }}
            </b-field>
          </b-field>
        </b-field>
        <div class="toolbar-bottom">
          <!-- Stands in for the rows above once they fold away: the plant, the loaded
               range and the stock limits are what users need in sight while scrolling
               a forecast that can run for months. -->
          <div v-show="isToolbarCondensed" class="toolbar-summary">
            <span v-if="selectedPlant" class="toolbar-summary-plant">{{ selectedPlant.name }}</span>
            <span>{{ toolbarDateRange }}</span>
            <span v-if="selectedTypes.length > 0">{{ selectedTypes.length }} {{ selectedTypes.length === 1 ? 'type' : 'types' }}</span>
            <b-switch v-model="shipmentView">
              Shipment View
            </b-switch>
            <template v-if="forecasts.length > 0">
              <span><b>Max</b> total {{ target.max_total }} &middot; clean {{ target.max_clean }}</span>
              <span><b>Safety</b> avail {{ target.min_available }} &middot; dirty {{ target.min_dirty }} &middot; repair {{ target.min_repair }} &middot; clean {{ target.min_clean }}</span>
            </template>
            <b-button
              v-if="undecidedCollsOut.length > 0 || undecidedCollsIn.length > 0"
              type="is-success"
              size="is-small"
              icon-left="box"
              @click="() => {isCheckingUndecided = true}"
            >
              Undated
            </b-button>
          </div>
          <b-button
            class="toolbar-toggle"
            type="is-ghost"
            size="is-small"
            :icon-left="isToolbarCondensed ? 'chevron-down' : 'chevron-up'"
            @click="() => {toggleToolbar()}"
          >
            {{ isToolbarCondensed ? 'Show controls' : 'Hide controls' }}
          </b-button>
        </div>
      </div>
      <!-- Holds the space the folded rows gave up. Without it the whole forecast
           yanks upwards the moment the bar shrinks. It sits at the top of the page,
           which is off screen for as long as the bar stays folded. -->
      <div :style="{ height: toolbarSpacerHeight + 'px' }" />
      <div
        v-if="shipmentView && pendingCount > 0"
        class="card px-4 py-3"
        style="position: fixed; bottom: 20px; left: 50%; transform: translateX(-50%); z-index: 20; display: flex; align-items: center; gap: 1rem; box-shadow: 0 2px 12px rgba(0,0,0,0.25); border: 2px solid rgb(255, 140, 0);"
      >
        <span style="font-weight: bold;">
          <b-icon icon="triangle-exclamation" size="is-small" style="color: rgb(255, 140, 0);" />
          {{ pendingCount }} unsaved shipment {{ pendingCount === 1 ? 'change' : 'changes' }}
        </span>
        <b-button
          type="is-success"
          icon-left="check"
          :loading="isSubmittingMoves"
          :disabled="isSubmittingMoves || isLoading"
          @click="() => { submitMoves() }"
        >
          Submit changes
        </b-button>
        <b-button
          type="is-light"
          icon-left="rotate-left"
          :disabled="isSubmittingMoves || isLoading"
          @click="() => { discardPending() }"
        >
          Discard
        </b-button>
      </div>
      <div
        v-for="(forecast, index) in forecasts"
        :key="forecast.date"
        class="card pl-2 py-1"
        :style="dayCardStyle(forecast)"
      >
        <div :style="{'text-transform': 'capitalize'}">
          <span :style="dayAccent(forecast) ? {'font-weight': 'bold'} : {}">
            {{ new Date(forecast.date).toLocaleDateString("en-EN", { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) }}
          </span>
          <b-tag v-if="isToday(forecast)" class="ml-2" :style="{'background-color': todayAccent, 'color': 'white'}">
            Today
          </b-tag>
          <b-tag v-if="isLastProduction(forecast)" class="ml-2" :style="{'background-color': lastProductionAccent, 'color': 'white'}">
            Last Production
          </b-tag>
          <template v-for="direction in ['in', 'out']">
            <b-tag
              v-if="forkliftDiscrepancy(forecast.date, direction)"
              :key="'forklift-' + direction"
              class="ml-2"
              :title="forkliftDiscrepancyTitle(forecast.date, direction)"
              :style="{'background-color': forkliftDiscrepancyAccent, 'color': 'white', 'text-transform': 'none'}"
            >
              Forklift {{ direction }} ≠ SAP — SAP used
            </b-tag>
          </template>
        </div>
        <div v-if="!shipmentView" class="columns">
          <div
            v-for="type in selectedTypes"
            :key="type.name"
            class="column"
          >
            <div style="border-bottom: 1px rgb(100, 100, 100) solid; padding-left: 0.3rem;">
              {{ type.name }}
            </div>
            <div
              v-for="status in forecast.statuses"
              :key="status"
              style="background-color: rgba(245, 222, 179, 0.8); padding-left: 0.3rem; color: black;"
            >
              {{ status }}
              <span :style="forecast.values[type.name][status].num_crates >= 0 ? forecast.values[type.name][status].changed ? {color: 'blue'} : {} : {color: 'red'}">
                {{ forecast.values[type.name][status].num_crates.toFixed(0) }}
              </span>
            </div>
            <div
              :style="{
                'border-top': '1px rgb(190, 190, 190) solid',
                'background-color': 'rgba(190, 230, 250, 1)',
                'padding-left': '0.3rem',
                'color': 'black'
              }"
            >
              {{ producedLabel(type.name) }} <span :style="{ 'color': producedColour(forecast, type.name) }">
                {{ producedFigure(forecast, type.name) }}
              </span>
            </div>
            <div
              :style="{
                'background-color': 'rgba(190, 230, 250, 1)',
                'padding-left': '0.3rem',
                'color': 'black'
              }"
            >
              {{ reverseLabel(type.name) }} <span :style="{ 'color': reverseColour(forecast, type.name) }">{{ reverseFigure(forecast, type.name) }}</span>
            </div>
            <div
              :style="{
                'cursor': 'pointer',
                'border-top': '1px rgb(190, 190, 190) solid',
                'background-color': 'rgba(180, 250, 160, 1)',
                'padding-left': '0.3rem',
                'color': 'black'
              }"
              @click="() => {collsInDisplayed = true; checkCollects(crateIOs[type.name][index])}"
            >
              In <span :title="forkliftDiscrepancyTitle(forecast.date, 'in')" :style="forkliftDiscrepancy(forecast.date, 'in') ? {'color': 'red', 'font-weight': 'bold'} : crateIOs[type.name][index].simIn ? {'color': 'green'} : crateIOs[type.name][index].doneCollsIn.length > 0 ? {} : crateIOs[type.name][index].predictedIn > 0 ? {'color': 'brown'} : crateIOs[type.name][index].predCollsIn.length > 0 ? {'color': 'blue'} : {}">{{ crateIOs[type.name][index].totalIn }}</span>
            </div>
            <div
              :style="{
                'cursor': 'pointer',
                'background-color': 'rgba(180, 250, 160, 1)',
                'padding-left': '0.3rem',
                'color': 'black'
              }"
              @click="() => {collsInDisplayed = false; checkCollects(crateIOs[type.name][index])}"
            >
              Out <span :title="forkliftDiscrepancyTitle(forecast.date, 'out')" :style="forkliftDiscrepancy(forecast.date, 'out') ? {'color': 'red', 'font-weight': 'bold'} : crateIOs[type.name][index].simOut ? {'color': 'green'} : crateIOs[type.name][index].doneCollsOut.length > 0 ? {} : crateIOs[type.name][index].predCollsOut.length > 0 ? {'color': 'blue'} : {}">{{ crateIOs[type.name][index].totalOut }}</span>
            </div>
          </div>
          <div class="column" :style="{ 'color': holidays.includes(new Date(forecast.date).toLocaleDateString('en-CA')) ? 'rgb(255, 255, 255)' : 'black' }">
            <div :style="{ 'font-weight': 'bold' }">
              Max
            </div>
            <table :style="{ 'border-spacing': '1rem 0px', 'border-collapse': 'separate' }">
              <tr>
                <td>{{ "Total:" }}</td>
                <td>{{ forecast.total.toFixed(0) }}</td>
                <td :style="{ color: (forecast.total/target.max_total * 100) > 80 ? 'red' : 'black' }">
                  {{ target.max_total && target.max_total !== 0 ? (forecast.total/target.max_total * 100).toFixed(0) + '%' : '' }}
                </td>
              </tr>
              <tr>
                <td>{{ "Clean:" }}</td>
                <td>{{ forecast.clean.toFixed(0) }}</td>
                <td :style="{ color: (forecast.clean/target.max_clean * 100) > 80 ? 'red' : 'black' }">
                  {{ target.max_clean && target.max_clean !== 0 ? (forecast.clean/target.max_clean * 100).toFixed(0) + '%' : '' }}
                </td>
              </tr>
            </table>
            <div :style="{ 'font-weight': 'bold' }">
              Safety
            </div>
            <table :style="{ 'border-spacing': '1rem 0px', 'border-collapse': 'separate' }">
              <tr>
                <td>{{ "Available:" }}</td>
                <td>{{ forecast.available.toFixed(0) }}</td>
                <td :style="{ color: (forecast.available/target.min_available * 100) < 100 ? 'red' : 'green' }">
                  {{ target.min_available && target.min_available !== 0 ? (forecast.available/target.min_available * 100).toFixed(0) + '%' : '' }}
                </td>
              </tr>
              <tr>
                <td>{{ "Dirty:" }}</td>
                <td>{{ forecast.dirty.toFixed(0) }}</td>
                <td :style="{ color: (forecast.dirty/target.min_dirty * 100) < 100 ? 'red' : 'green' }">
                  {{ target.min_dirty && target.min_dirty !== 0 ? (forecast.dirty/target.min_dirty * 100).toFixed(0) + '%' : '' }}
                </td>
              </tr>
              <tr>
                <td>{{ "Repair:" }}</td>
                <td>{{ forecast.repair.toFixed(0) }}</td>
                <td :style="{ color: (forecast.repair/target.min_repair * 100) < 100 ? 'red' : 'green' }">
                  {{ target.min_repair && target.min_repair !== 0 ? (forecast.repair/target.min_repair * 100).toFixed(0) + '%' : '' }}
                </td>
              </tr>
              <tr>
                <td>{{ "Clean:" }}</td>
                <td>{{ forecast.clean.toFixed(0) }}</td>
                <td :style="{ color: (forecast.clean/target.min_clean * 100) < 100 ? 'red' : 'green' }">
                  {{ target.min_clean && target.min_clean !== 0 ? (forecast.clean/target.min_clean * 100).toFixed(0) + '%' : '' }}
                </td>
              </tr>
            </table>
          </div>
          <b-button
            class="left-right mr-2"
            type="is-ghost"
            icon-left="door-closed"
            :style="{'display': 'block', 'position': 'absolute', 'right': '60px', 'margin-top': '-10px', 'color': 'black' }"
            :disabled="isLoading"
            @click="() => { addHoliday(new Date(forecast.date).toLocaleDateString('en-CA')) }"
          />
          <b-button
            v-if="holidays.includes(new Date(forecast.date).toLocaleDateString('en-CA'))"
            class="left-right mr-2"
            type="is-ghost"
            icon-left="x"
            :style="{'display': 'block', 'position': 'absolute', 'right': '60px', 'margin-top': '-10px', 'color': 'red' }"
            :disabled="isLoading"
            @click="() => { removeHoliday(new Date(forecast.date).toLocaleDateString('en-CA')) }"
          />
          <b-tooltip
            :label="forecast.comment?.text"
            :active="forecast.comment ? true : false"
            multilined
          >
            <b-button
              class="left-right mr-2"
              type="is-ghost"
              :icon-left="forecast.comment ? 'star' : 'comment'"
              :style="{'display': 'block', 'position': 'absolute', 'right': '30px', 'margin-top': '-10px', 'color': forecast.comment ? 'gold' : 'gray'}"
              :disabled="isLoading"
              @click="() => {editComment(forecast)}"
            />
          </b-tooltip>
          <b-button
            class="left-right mr-2"
            type="is-ghost"
            icon-left="edit"
            :style="{'display': 'block', 'position': 'absolute', 'right': '0', 'margin-top': '-10px'}"
            :disabled="isLoading"
            @click="() => {editForecast(forecast)}"
          />
        </div>
        <div
          v-if="shipmentView"
          :style="{
            'position': 'relative',
            'border-radius': '4px',
            'outline': dragOverDate === forecast.date ? '3px dashed rgb(70, 110, 220)' : 'none',
            'outline-offset': '2px'
          }"
          @dragover.prevent="onDragOver($event, forecast.date)"
          @drop.prevent="onDrop(forecast.date)"
        >
          <div :style="{'display': 'flex', 'justify-content': 'flex-end', 'align-items': 'center', 'gap': '0.5rem', 'padding': '0 0.25rem 0.25rem'}">
            <span v-if="dragOverDate === forecast.date" :style="{'font-size': '0.8rem', 'color': 'rgb(70, 110, 220)', 'font-weight': 'bold'}">
              Drop to move the shipment here
            </span>
            <b-button
              size="is-small"
              type="is-info"
              icon-left="plus"
              @click="() => { openNewPrediction(forecast.date) }"
            >
              Add prediction
            </b-button>
          </div>
          <div
            :style="{
              'display': 'flex',
              'flex-direction': 'row',
              'justify-content': 'space-around'
            }"
          >
            <div
              :style="{
                'height': '15rem',
                'max-height': '15rem',
                'overflow-y': 'scroll',
                '-ms-overflow-style': 'none',
                'scrollbarWidth': '0',
                'width': '45%'
              }"
            >
              <table
                :style="{
                  'display': 'flex',
                  'flex-direction': 'column',
                  'border': '2px solid rgb(140 140 140)',
                  'width' : '100%',
                  'border-spacing': '0.5rem 0px',
                  'border-collapse': 'collapse',
                  'text-align': 'left'
                }"
              >
                <thead style="position: sticky; top: 0; background-color: lightgray; display: table; width: 100%;">
                  <tr>
                    <td style="width: 10ch; max-width: 10ch; overflow: hidden; white-space: nowrap;">
                      Origin
                    </td>
                    <td style="width: 10ch; max-width: 10ch;">
                      OB
                    </td>
                    <td style="width: 6ch; max-width: 6ch;">
                      RO
                    </td>
                    <td style="width: 4ch; max-width: 4ch;">
                      Nbr
                    </td>
                    <td style="width: 4ch; max-width: 4ch;">
                      Type
                    </td>
                    <td style="width: 6ch; max-width: 6ch;">
                      Status
                    </td>
                  </tr>
                </thead>
                <tbody style="width: 100%; display: table;">
                  <tr
                    v-for="move in forecast.dayMoves.doneCollsIn"
                    :key="'donecollsin' + move._key"
                    :style="{
                      'color': move.relic ? 'blue' : 'black',
                      'font-weight': move.naval ? 'bold' : 'regular',
                      'background-color': move.shipType === 'COL'? 'rgb(162, 200, 215)' : 'rgb(162, 200, 255)',
                      'border-top': 'solid 1px rgb(220, 220, 220)',
                      'border-left': 'solid 1px rgb(220, 220, 220)',
                      'border-right': 'solid 1px rgb(220, 220, 220)'
                    }"
                  >
                    <td style="width: 10ch; max-width: 10ch; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                      {{ move.name }}
                    </td>
                    <td style="width: 10ch; max-width: 10ch;">
                      {{ displayedOb(move) }}
                    </td>
                    <td style="width: 6ch; max-width: 6ch;">
                      {{ move.shipment }}
                    </td>
                    <td style="width: 4ch; max-width: 4ch;">
                      {{ parseInt(move.num_crates) }}
                    </td>
                    <td style="width: 4ch; max-width: 4ch;">
                      {{ move.type }}
                    </td>
                    <td style="width: 6ch; max-width: 6ch;">
                      {{ move.status }}
                    </td>
                  </tr>
                  <tr
                    v-for="move in forecast.dayMoves.predCollsIn"
                    :key="'predcollsin' + move._key"
                    :draggable="isMovable(move)"
                    :title="moveTitle(move)"
                    :style="{
                      'color': move.relic ? 'blue' : 'black',
                      'font-weight': move.naval ? 'bold' : 'regular',
                      'background-color': move.shipType === 'COL'? 'rgb(162, 200, 215)' : 'rgb(162, 200, 255)',
                      'border-top': 'solid 1px rgb(220, 220, 220)',
                      'border-left': 'solid 1px rgb(220, 220, 220)',
                      'border-right': 'solid 1px rgb(220, 220, 220)',
                      'outline': moveOutline(move),
                      'outline-offset': '-2px',
                      'cursor': isMovable(move) ? 'grab' : 'default'
                    }"
                    @dragstart="onDragStart($event, move)"
                    @dragend="onDragEnd"
                  >
                    <td style="width: 10ch; max-width: 10ch; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                      {{ move.name }}
                    </td>
                    <td style="width: 10ch; max-width: 10ch;">
                      {{ displayedOb(move) }}
                    </td>
                    <td style="width: 6ch; max-width: 6ch;">
                      {{ move.shipment }}
                    </td>
                    <td style="width: 4ch; max-width: 4ch;">
                      {{ parseInt(move.num_crates) }}
                    </td>
                    <td style="width: 4ch; max-width: 4ch;">
                      {{ move.type }}
                    </td>
                    <td style="width: 6ch; max-width: 6ch;">
                      {{ move.status }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div
              :style="{
                'height': '15rem',
                'max-height': '15rem',
                'overflow-y': 'scroll',
                'width': '45%'
              }"
            >
              <table
                :style="{
                  'display': 'flex',
                  'flex-direction': 'column',
                  'border': '2px solid rgb(140 140 140)',
                  'width' : '100%',
                  'border-spacing': '1rem 0px',
                  'border-collapse': 'collapse',
                  'text-align': 'left'
                }"
              >
                <thead style="position: sticky; top: 0; background-color: lightgray; display: table; width: 100%;">
                  <tr>
                    <td style="width: 10ch; max-width: 10ch; overflow: hidden; white-space: nowrap;">
                      Destination
                    </td>
                    <td style="width: 10ch; max-width: 10ch;">
                      OB
                    </td>
                    <td style="width: 6ch; max-width: 6ch;">
                      RO
                    </td>
                    <td style="width: 4ch; max-width: 4ch;">
                      Nbr
                    </td>
                    <td style="width: 4ch; max-width: 4ch;">
                      Type
                    </td>
                    <td style="width: 6ch; max-width: 6ch;">
                      Status
                    </td>
                  </tr>
                </thead>
                <tbody style="width: 100%; display: table;">
                  <tr
                    v-for="move in forecast.dayMoves.doneCollsOut"
                    :key="'donecollsout' + move._key"
                    :style="{
                      'color': move.relic ? 'blue' : 'black',
                      'font-weight': move.naval ? 'bold' : 'regular',
                      'background-color': move.shipType === 'DEL'? 'rgb(146, 208, 80)': 'rgb(251, 226, 213)',
                      'border-top': 'solid 1px rgb(220, 220, 220)',
                      'border-left': 'solid 1px rgb(220, 220, 220)',
                      'border-right': 'solid 1px rgb(220, 220, 220)'
                    }"
                  >
                    <td style="width: 10ch; max-width: 10ch; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                      {{ move.name }}
                    </td>
                    <td style="width: 10ch; max-width: 10ch;">
                      {{ displayedOb(move) }}
                    </td>
                    <td style="width: 6ch; max-width: 6ch;">
                      {{ move.shipment }}
                    </td>
                    <td style="width: 4ch; max-width: 4ch;">
                      {{ parseInt(move.num_crates) }}
                    </td>
                    <td style="width: 4ch; max-width: 4ch;">
                      {{ move.type }}
                    </td>
                    <td style="width: 6ch; max-width: 6ch;">
                      {{ move.status }}
                    </td>
                  </tr>
                  <tr
                    v-for="move in forecast.dayMoves.predCollsOut"
                    :key="'predcollsout' + move._key"
                    :draggable="isMovable(move)"
                    :title="moveTitle(move)"
                    :style="{
                      'color': move.relic ? 'blue' : 'black',
                      'font-weight': move.naval ? 'bold' : 'regular',
                      'background-color': move.shipType === 'DEL'? 'rgb(146, 208, 80)': 'rgb(251, 226, 213)',
                      'border-top': 'solid 1px rgb(220, 220, 220)',
                      'border-left': 'solid 1px rgb(220, 220, 220)',
                      'border-right': 'solid 1px rgb(220, 220, 220)',
                      'outline': moveOutline(move),
                      'outline-offset': '-2px',
                      'cursor': isMovable(move) ? 'grab' : 'default'
                    }"
                    @dragstart="onDragStart($event, move)"
                    @dragend="onDragEnd"
                  >
                    <td style="width: 10ch; max-width: 10ch; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                      {{ move.name }}
                    </td>
                    <td style="width: 10ch; max-width: 10ch;">
                      {{ displayedOb(move) }}
                    </td>
                    <td style="width: 6ch; max-width: 6ch;">
                      {{ move.shipment }}
                    </td>
                    <td style="width: 4ch; max-width: 4ch;">
                      {{ parseInt(move.num_crates) }}
                    </td>
                    <td style="width: 4ch; max-width: 4ch;">
                      {{ move.type }}
                    </td>
                    <td style="width: 6ch; max-width: 6ch;">
                      {{ move.status }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
    <b-modal key="undecided" :active.sync="isCheckingUndecided" scroll="keep" has-modal-card>
      <div v-if="isCheckingUndecided" style="margin-left: -20vw;">
        <div class="modal-card" style="min-width: 70vw; width: fit-content; min-height: 50vh; height: fit-content;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Undated Movements
              <span v-if="storedName" style="font-size:1rem; text-overflow: elipsis">{{ storedName }}</span>
            </p>
          </header>
          <section class="modal-card-body">
            <div style="display: flex; align-items: center; margin-top: -20px; margin-bottom: 20px">
              View Crates OUT
              <b-switch v-model="collsInDisplayed" style="margin-left: 10px;" />
              View Crates IN
              <b-button
                size="is-small"
                style="margin-left: 20px;"
                @click="hideOldUndecided = !hideOldUndecided"
              >
                {{ hideOldUndecided ? 'Show older movements' : 'Hide older movements' }}
              </b-button>
              <div style="margin-left: auto; font-weight: bold;">
                Total (last week): {{ collsInDisplayed ? totalRecentUndecidedIn : totalRecentUndecidedOut }}
                <span style="margin-left: 15px;">
                  Total (all): {{ collsInDisplayed ? totalUndecidedIn : totalUndecidedOut }}
                </span>
              </div>
            </div>
            <div v-if="collsInDisplayed">
              <div class="columns" style="background-color: rgb(255, 255, 255);">
                <div class="column">
                  Origin
                </div>
                <div class="column">
                  OB
                </div>
                <div class="column">
                  RO
                </div>
                <div class="column">
                  Quantity
                </div>
                <div class="column">
                  Type
                </div>
                <div class="column">
                  Status
                </div>
                <div class="column">
                  Date
                </div>
              </div>
              <div
                v-for="undecidedCollIn in displayedUndecidedCollsIn"
                :key="undecidedCollIn.id"
                class="columns"
                :style="{
                  'color': undecidedCollIn.relic ? 'blue' : 'black',
                  'font-weight': undecidedCollIn.naval ? 'bold' : 'regular',
                  'background-color': 'rgb(162, 200, 215)',
                  'border-top': 'solid 1px rgb(220, 220, 220)',
                  'border-left': 'solid 1px rgb(220, 220, 220)',
                  'border-right': 'solid 1px rgb(220, 220, 220)'
                }"
              >
                <div class="shipping-detail">
                  <b-tooltip :label="undecidedCollIn.name" position="is-right">
                    <div style="overflow: hidden; text-overflow: ellipsis; text-wrap: nowrap; width: 18ch;">
                      {{ undecidedCollIn.name }}
                    </div>
                  </b-tooltip>
                </div>
                <div class="shipping-detail">
                  {{ displayedOb(undecidedCollIn) }}
                </div>
                <div class="shipping-detail">
                  {{ undecidedCollIn.shipment }}
                </div>
                <div class="shipping-detail">
                  {{ parseInt(undecidedCollIn.num_crates) }}
                </div>
                <div class="shipping-detail">
                  {{ undecidedCollIn.type }}
                </div>
                <div class="shipping-detail">
                  {{ undecidedCollIn.status }}
                </div>
                <div class="shipping-detail">
                  {{ undecidedCollIn.originDate }}
                </div>
              </div>
            </div>
            <div v-if="!collsInDisplayed">
              <div class="columns" style="background-color: rgb(255, 255, 255);">
                <div class="column">
                  Destination
                </div>
                <div class="column">
                  OB
                </div>
                <div class="column">
                  RO
                </div>
                <div class="column">
                  Quantity
                </div>
                <div class="column">
                  Type
                </div>
                <div class="column">
                  Status
                </div>
                <div class="column">
                  Date
                </div>
              </div>
              <div
                v-for="undecidedCollOut in displayedUndecidedCollsOut"
                :key="undecidedCollOut.id"
                class="columns"
                :style="{
                  'color': 'black',
                  'font-weight': 'regular',
                  'background-color': undecidedCollOut.shipType === 'DEL'? 'rgb(146, 208, 80)': 'rgb(251, 226, 213)',
                  'border-top': 'solid 1px rgb(220, 220, 220)',
                  'border-left': 'solid 1px rgb(220, 220, 220)',
                  'border-right': 'solid 1px rgb(220, 220, 220)'
                }"
              >
                <div class="shipping-detail">
                  <b-tooltip :label="undecidedCollOut.name" position="is-right">
                    <div style="overflow: hidden; text-overflow: ellipsis; text-wrap: nowrap; width: 18ch;">
                      {{ undecidedCollOut.name }}
                    </div>
                  </b-tooltip>
                </div>
                <div class="shipping-detail">
                  {{ displayedOb(undecidedCollOut) }}
                </div>
                <div class="shipping-detail">
                  {{ undecidedCollOut.shipment }}
                </div>
                <div class="shipping-detail">
                  {{ parseInt(undecidedCollOut.num_crates) }}
                </div>
                <div class="shipping-detail">
                  {{ undecidedCollOut.type }}
                </div>
                <div class="shipping-detail">
                  {{ undecidedCollOut.status }}
                </div>
                <div class="shipping-detail">
                  {{ undecidedCollOut.originDate }}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </b-modal>
    <b-modal :active.sync="isImportingExcel" scroll="keep" has-modal-card>
      <div v-if="isImportingExcel" style="margin-left: -20vw;">
        <div class="modal-card" style="min-width: 70vw; width: fit-content; min-height: 50vh; height: fit-content;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Import Excel Simulations
              <span v-if="storedName" style="font-size:1rem; text-overflow: elipsis">{{ storedName }}</span>
            </p>
          </header>
          <section class="modal-card-body">
            <b-field grouped>
              <b-field>
                <input
                  id="fileUpload"
                  type="file"
                  accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  hidden
                  @change="loadFile"
                >
                <b-button class="btn" @click="chooseFiles">
                  Choose File
                </b-button>
              </b-field>
              <b-field>
                <b-tooltip :active="tooManyTypesForExcel" :label="excelDisabledReason" multilined>
                  <b-button
                    class="left-right"
                    type="is-warning"
                    icon-left="x"
                    :disabled="!loadedExcel || tooManyTypesForExcel"
                    @click="() => {testExcel()}"
                  >
                    Read Simulations
                  </b-button>
                </b-tooltip>
              </b-field>
            </b-field>
            <div v-if="days && days.length > 0" style="margin-top: 5px; margin-bottom: 20px;">
              <div v-for="(day, index) in days" :key="'simulation' + index">
                <b-field grouped style="background-color: white; padding: 5px; width: fit-content;">
                  <b-field>{{ new Date(day.date).toLocaleDateString('en-CA') }}</b-field>
                  <b-field>{{ day.type }}</b-field>
                  <b-field>{{ day.operation }}</b-field>
                  <b-field>{{ day.num_crates }}</b-field>
                </b-field>
              </div>
            </div>
            <div v-if="newFalseShipments && newFalseShipments.length > 0" style="margin-top: 5px; margin-bottom: 20px;">
              <div v-for="(newFalseShipment, index) in newFalseShipments" :key="'simulation' + index">
                <b-field grouped style="background-color: white; padding: 5px; width: fit-content;">
                  <b-field>{{ new Date(newFalseShipment.date).toLocaleDateString('en-CA') }}</b-field>
                  <b-field>{{ newFalseShipment.name }}</b-field>
                  <b-field>{{ newFalseShipment.qty }}</b-field>
                </b-field>
              </div>
            </div>
            <b-field>
              <b-button
                class="left-right"
                type="is-warning"
                icon-left="x"
                :loading="isSaving"
                :disabled="isSaving || (days.length <= 0 && newFalseShipments.length <= 0)"
                @click="() => {saveSimulations()}"
              >
                Save Simulations
              </b-button>
            </b-field>
          </section>
        </div>
      </div>
    </b-modal>

    <b-modal :key="'resetModal'" :active.sync="isResetting" scroll="keep" has-modal-card>
      <div v-if="isResetting" style="margin-left: -20vw;">
        <div class="modal-card" style="min-width: 70vw; width: fit-content; min-height: 60vh; height: fit-content;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Reset Fake Data
              <span v-if="storedName" style="font-size:1rem; text-overflow: elipsis">{{ storedName }}</span>
            </p>
          </header>
          <section class="modal-card-body">
            <b-field grouped>
              <b-button
                class="left-right"
                type="is-warning"
                icon-left="x"
                :disabled="isLoading"
                @click="() => {resetSimulations({ simulations: true })}"
              >
                Reset Simulations
              </b-button>
              <b-button
                class="left-right ml-3"
                type="is-warning"
                icon-left="x"
                :disabled="isLoading"
                @click="() => {resetSimulations({ falseShipments: true })}"
              >
                Reset Fake Shipments
              </b-button>
              <b-button
                class="left-right ml-3"
                type="is-warning"
                icon-left="x"
                :disabled="isLoading"
                @click="() => {resetSimulations({ movedShipments: true })}"
              >
                Reset Moved Shipments
              </b-button>
              <b-button
                class="left-right ml-3"
                type="is-danger"
                icon-left="trash"
                :disabled="isLoading"
                @click="() => {resetSimulations({ simulations: true, falseShipments: true, movedShipments: true })}"
              >
                Reset All
              </b-button>
            </b-field>
            <p style="margin-bottom: 15px; color: rgb(120, 120, 120);">
              Most recently created content for this site. Resets cannot be undone.
            </p>
            <b-field grouped group-multiline>
              <b-field label="Show">
                <b-select v-model="resetTableType" @input="() => {selectedResetRows = []; clearResetFilters()}">
                  <option value="simulations">
                    Simulations
                  </option>
                  <option value="falseShipments">
                    Fake Shipments
                  </option>
                  <option value="movedShipments">
                    Moved Shipments
                  </option>
                </b-select>
              </b-field>
              <b-field :label="'\u00a0'">
                <b-button
                  type="is-danger"
                  icon-left="trash"
                  :disabled="isLoading || selectedResetRows.length === 0"
                  @click="() => {archiveSelected()}"
                >
                  Archive Selected ({{ selectedResetRows.length }})
                </b-button>
              </b-field>
            </b-field>
            <div
              :style="{
                'max-height': '24rem',
                'overflow-y': 'auto',
                'border': '1px solid rgb(200, 200, 200)'
              }"
            >
              <div
                v-if="resetTableType === 'simulations'"
                class="columns"
                style="position: sticky; top: 0; background-color: lightgray; margin: 0; font-weight: bold;"
              >
                <div class="column is-1">
                  <b-checkbox v-model="allResetRowsSelected" />
                </div>
                <div
                  class="column"
                  :style="{ 'cursor': resetFilterOpen.date ? 'default' : 'pointer', 'user-select': 'none' }"
                  :title="resetFilterOpen.date ? null : 'Filter by date'"
                  @click="resetFilterOpen.date = true"
                >
                  <div style="display: flex; align-items: center; gap: 0.4rem;">
                    Date
                    <a
                      :title="resetSortDesc ? 'Newest first' : 'Oldest first'"
                      style="color: inherit;"
                      @click.stop="resetSortDesc = !resetSortDesc"
                    >
                      <b-icon :icon="resetSortDesc ? 'caret-down' : 'caret-up'" size="is-small" />
                    </a>
                    <template v-if="resetFilterOpen.date">
                      <b-datepicker
                        v-model="resetFilters.date"
                        :first-day-of-week="1"
                        :locale="'en-CA'"
                        size="is-small"
                        placeholder="Any date"
                        style="max-width: 7rem;"
                        append-to-body
                      />
                      <a title="Clear filter" style="color: inherit;" @click.stop="closeResetFilter('date')">
                        <b-icon icon="x" size="is-small" />
                      </a>
                    </template>
                  </div>
                </div>
                <div
                  class="column"
                  :style="{ 'cursor': resetFilterOpen.type ? 'default' : 'pointer', 'user-select': 'none' }"
                  :title="resetFilterOpen.type ? null : 'Filter by type'"
                  @click="resetFilterOpen.type = true"
                >
                  <div style="display: flex; align-items: center; gap: 0.4rem;">
                    Type
                    <template v-if="resetFilterOpen.type">
                      <b-select v-model="resetFilters.type" size="is-small">
                        <option value="">
                          All
                        </option>
                        <option v-for="value in resetTypeOptions" :key="'resettype' + value" :value="value">
                          {{ value }}
                        </option>
                      </b-select>
                      <a title="Clear filter" style="color: inherit;" @click.stop="closeResetFilter('type')">
                        <b-icon icon="x" size="is-small" />
                      </a>
                    </template>
                  </div>
                </div>
                <div
                  class="column"
                  :style="{ 'cursor': resetFilterOpen.operation ? 'default' : 'pointer', 'user-select': 'none' }"
                  :title="resetFilterOpen.operation ? null : 'Filter by operation'"
                  @click="resetFilterOpen.operation = true"
                >
                  <div style="display: flex; align-items: center; gap: 0.4rem;">
                    Operation
                    <template v-if="resetFilterOpen.operation">
                      <b-select v-model="resetFilters.operation" size="is-small">
                        <option value="">
                          All
                        </option>
                        <option v-for="value in resetOperationOptions" :key="'resetop' + value" :value="value">
                          {{ value }}
                        </option>
                      </b-select>
                      <a title="Clear filter" style="color: inherit;" @click.stop="closeResetFilter('operation')">
                        <b-icon icon="x" size="is-small" />
                      </a>
                    </template>
                  </div>
                </div>
                <div
                  class="column"
                  :style="{ 'cursor': resetFilterOpen.crates ? 'default' : 'pointer', 'user-select': 'none' }"
                  :title="resetFilterOpen.crates ? null : 'Filter by crates'"
                  @click="resetFilterOpen.crates = true"
                >
                  <div style="display: flex; align-items: center; gap: 0.4rem;">
                    Crates
                    <template v-if="resetFilterOpen.crates">
                      <b-input
                        v-model="resetFilters.crates"
                        type="number"
                        step="1"
                        size="is-small"
                        placeholder="Any"
                        style="max-width: 5rem;"
                      />
                      <a title="Clear filter" style="color: inherit;" @click.stop="closeResetFilter('crates')">
                        <b-icon icon="x" size="is-small" />
                      </a>
                    </template>
                  </div>
                </div>
              </div>
              <div
                v-else-if="resetTableType === 'movedShipments'"
                class="columns"
                style="position: sticky; top: 0; background-color: lightgray; margin: 0; font-weight: bold;"
              >
                <div class="column is-1">
                  <b-checkbox v-model="allResetRowsSelected" />
                </div>
                <div
                  class="column"
                  :style="{ 'cursor': resetFilterOpen.date ? 'default' : 'pointer', 'user-select': 'none' }"
                  :title="resetFilterOpen.date ? null : 'Filter by date'"
                  @click="resetFilterOpen.date = true"
                >
                  <div style="display: flex; align-items: center; gap: 0.4rem;">
                    Date
                    <a
                      :title="resetSortDesc ? 'Newest first' : 'Oldest first'"
                      style="color: inherit;"
                      @click.stop="resetSortDesc = !resetSortDesc"
                    >
                      <b-icon :icon="resetSortDesc ? 'caret-down' : 'caret-up'" size="is-small" />
                    </a>
                    <template v-if="resetFilterOpen.date">
                      <b-datepicker
                        v-model="resetFilters.date"
                        :first-day-of-week="1"
                        :locale="'en-CA'"
                        size="is-small"
                        placeholder="Any date"
                        style="max-width: 7rem;"
                        append-to-body
                      />
                      <a title="Clear filter" style="color: inherit;" @click.stop="closeResetFilter('date')">
                        <b-icon icon="x" size="is-small" />
                      </a>
                    </template>
                  </div>
                </div>
                <div class="column">
                  OB
                </div>
                <div
                  class="column"
                  :style="{ 'cursor': resetFilterOpen.operation ? 'default' : 'pointer', 'user-select': 'none' }"
                  :title="resetFilterOpen.operation ? null : 'Filter by operation'"
                  @click="resetFilterOpen.operation = true"
                >
                  <div style="display: flex; align-items: center; gap: 0.4rem;">
                    Operation
                    <template v-if="resetFilterOpen.operation">
                      <b-select v-model="resetFilters.operation" size="is-small">
                        <option value="">
                          All
                        </option>
                        <option v-for="value in resetOperationOptions" :key="'resetop' + value" :value="value">
                          {{ value }}
                        </option>
                      </b-select>
                      <a title="Clear filter" style="color: inherit;" @click.stop="closeResetFilter('operation')">
                        <b-icon icon="x" size="is-small" />
                      </a>
                    </template>
                  </div>
                </div>
                <div class="column">
                  Origin / Dest.
                </div>
                <div
                  class="column"
                  :style="{ 'cursor': resetFilterOpen.type ? 'default' : 'pointer', 'user-select': 'none' }"
                  :title="resetFilterOpen.type ? null : 'Filter by type'"
                  @click="resetFilterOpen.type = true"
                >
                  <div style="display: flex; align-items: center; gap: 0.4rem;">
                    Type
                    <template v-if="resetFilterOpen.type">
                      <b-select v-model="resetFilters.type" size="is-small">
                        <option value="">
                          All
                        </option>
                        <option v-for="value in resetTypeOptions" :key="'resettype' + value" :value="value">
                          {{ value }}
                        </option>
                      </b-select>
                      <a title="Clear filter" style="color: inherit;" @click.stop="closeResetFilter('type')">
                        <b-icon icon="x" size="is-small" />
                      </a>
                    </template>
                  </div>
                </div>
                <div class="column">
                  Status
                </div>
                <div
                  class="column"
                  :style="{ 'cursor': resetFilterOpen.crates ? 'default' : 'pointer', 'user-select': 'none' }"
                  :title="resetFilterOpen.crates ? null : 'Filter by crates'"
                  @click="resetFilterOpen.crates = true"
                >
                  <div style="display: flex; align-items: center; gap: 0.4rem;">
                    Crates
                    <template v-if="resetFilterOpen.crates">
                      <b-input
                        v-model="resetFilters.crates"
                        type="number"
                        step="1"
                        size="is-small"
                        placeholder="Any"
                        style="max-width: 5rem;"
                      />
                      <a title="Clear filter" style="color: inherit;" @click.stop="closeResetFilter('crates')">
                        <b-icon icon="x" size="is-small" />
                      </a>
                    </template>
                  </div>
                </div>
                <div class="column">
                  Kind
                </div>
              </div>
              <div
                v-else
                class="columns"
                style="position: sticky; top: 0; background-color: lightgray; margin: 0; font-weight: bold;"
              >
                <div class="column is-1">
                  <b-checkbox v-model="allResetRowsSelected" />
                </div>
                <div
                  class="column"
                  :style="{ 'cursor': resetFilterOpen.date ? 'default' : 'pointer', 'user-select': 'none' }"
                  :title="resetFilterOpen.date ? null : 'Filter by date'"
                  @click="resetFilterOpen.date = true"
                >
                  <div style="display: flex; align-items: center; gap: 0.4rem;">
                    Date
                    <a
                      :title="resetSortDesc ? 'Newest first' : 'Oldest first'"
                      style="color: inherit;"
                      @click.stop="resetSortDesc = !resetSortDesc"
                    >
                      <b-icon :icon="resetSortDesc ? 'caret-down' : 'caret-up'" size="is-small" />
                    </a>
                    <template v-if="resetFilterOpen.date">
                      <b-datepicker
                        v-model="resetFilters.date"
                        :first-day-of-week="1"
                        :locale="'en-CA'"
                        size="is-small"
                        placeholder="Any date"
                        style="max-width: 7rem;"
                        append-to-body
                      />
                      <a title="Clear filter" style="color: inherit;" @click.stop="closeResetFilter('date')">
                        <b-icon icon="x" size="is-small" />
                      </a>
                    </template>
                  </div>
                </div>
                <div class="column">
                  Name
                </div>
                <div
                  class="column"
                  :style="{ 'cursor': resetFilterOpen.crates ? 'default' : 'pointer', 'user-select': 'none' }"
                  :title="resetFilterOpen.crates ? null : 'Filter by quantity'"
                  @click="resetFilterOpen.crates = true"
                >
                  <div style="display: flex; align-items: center; gap: 0.4rem;">
                    Quantity
                    <template v-if="resetFilterOpen.crates">
                      <b-input
                        v-model="resetFilters.crates"
                        type="number"
                        step="1"
                        size="is-small"
                        placeholder="Any"
                        style="max-width: 5rem;"
                      />
                      <a title="Clear filter" style="color: inherit;" @click.stop="closeResetFilter('crates')">
                        <b-icon icon="x" size="is-small" />
                      </a>
                    </template>
                  </div>
                </div>
              </div>
              <div
                v-for="(row, index) in resetTableRows"
                :key="'resetrow' + index"
                class="columns"
                :style="{
                  'margin': '0',
                  'background-color': index % 2 === 0 ? 'rgb(245, 245, 245)' : 'white'
                }"
              >
                <div class="column is-1">
                  <b-checkbox v-model="selectedResetRows" :native-value="row.id" />
                </div>
                <template v-if="resetTableType === 'simulations'">
                  <div class="column">
                    {{ resetRowDate(row) }}
                  </div>
                  <div class="column">
                    {{ row.type }}
                  </div>
                  <div class="column">
                    {{ row.operation }}
                  </div>
                  <div class="column">
                    {{ parseInt(row.num_crates) }}
                  </div>
                </template>
                <template v-else-if="resetTableType === 'movedShipments'">
                  <div class="column">
                    {{ resetRowDate(row) }}
                  </div>
                  <div class="column">
                    {{ row.is_new ? 'PREVISION' : movedOb(row) }}
                  </div>
                  <div class="column">
                    {{ row.shipment_type }}
                  </div>
                  <div
                    class="column"
                    :title="movedCounterparty(row)"
                    style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;"
                  >
                    {{ movedCounterparty(row) }}
                  </div>
                  <div class="column">
                    {{ row.crate_type }}
                  </div>
                  <div class="column">
                    {{ row.crate_status }}
                  </div>
                  <div class="column">
                    {{ row.num_crates !== null && row.num_crates !== undefined ? parseInt(row.num_crates) : '—' }}
                  </div>
                  <div class="column">
                    {{ row.is_new ? 'New' : 'Move' }}
                  </div>
                </template>
                <template v-else>
                  <div class="column">
                    {{ resetRowDate(row) }}
                  </div>
                  <div class="column">
                    {{ row.name }}
                  </div>
                  <div class="column">
                    {{ parseInt(row.qty) }}
                  </div>
                </template>
              </div>
              <div v-if="resetTableRows.length === 0" style="padding: 1rem; text-align: center; color: rgb(150, 150, 150);">
                No {{ resetTableType === 'simulations' ? 'simulations' : resetTableType === 'movedShipments' ? 'moved shipments' : 'fake shipments' }} {{ resetFiltersActive ? 'match these filters' : 'for this site' }}.
              </div>
            </div>
          </section>
        </div>
      </div>
    </b-modal>

    <b-modal :key="'predictionModal'" :active.sync="isAddingPrediction" scroll="keep" has-modal-card>
      <form v-if="isAddingPrediction" @submit.prevent="addPrediction">
        <div class="modal-card" style="min-width: 40vw; width: fit-content; height: fit-content;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              New predicted shipment
              <span v-if="storedName" style="font-size:1rem;">{{ storedName }}</span>
            </p>
          </header>
          <section class="modal-card-body">
            <p style="margin-bottom: 15px; color: rgb(120, 120, 120);">
              Staged locally. It appears on the forecast immediately and is saved when you submit your changes.
            </p>
            <b-field grouped>
              <b-field label="Date">
                <b-datepicker
                  v-model="predictionDate"
                  name="predictionDate"
                  :first-day-of-week="1"
                  :locale="'en-CA'"
                  icon="calendar-alt"
                  placeholder="Arrival / departure date"
                />
              </b-field>
              <b-field label="Requirement type">
                <b-select v-model="newPrediction.shipment_type">
                  <option value="COL">
                    COL (collect, in)
                  </option>
                  <option value="TRFI">
                    TRFI (transfer in)
                  </option>
                  <option value="NCR">
                    NCR (in)
                  </option>
                  <option value="DEL">
                    DEL (delivery, out)
                  </option>
                  <option value="TRFO">
                    TRFO (transfer out)
                  </option>
                </b-select>
              </b-field>
            </b-field>
            <b-field grouped>
              <b-field label="Crate type">
                <b-select v-model="newPrediction.crate_type">
                  <option v-for="t in siteTypes" :key="'ptype' + t.name" :value="t.name === '5.X' ? '5.2' : t.name">
                    {{ t.name === '5.X' ? '5.2' : t.name }}
                  </option>
                </b-select>
              </b-field>
              <!--
                A client type holds prepared, already-clean crates only, so predicting
                one dirty or in repair would put stock in a cell nothing can drain.
              -->
              <b-field label="Status">
                <b-select v-model="newPrediction.crate_status">
                  <option value="CLEAN">
                    CLEAN
                  </option>
                  <option v-if="!isClientType(newPrediction.crate_type)" value="DIRTY">
                    DIRTY
                  </option>
                  <option v-if="!isClientType(newPrediction.crate_type)" value="REPAIR">
                    REPAIR
                  </option>
                </b-select>
              </b-field>
              <b-field label="Quantity">
                <b-input v-model="newPrediction.num_crates" type="number" min="1" step="1" />
              </b-field>
            </b-field>
            <b-field grouped>
              <b-field label="Ship from" expanded>
                <b-input v-if="!predictionIsIn" :value="storedPlantId" readonly />
                <b-input v-else v-model="newPrediction.ship_from" placeholder="Origin code" />
              </b-field>
              <b-field label="Ship to" expanded>
                <b-input v-if="predictionIsIn" :value="storedPlantId" readonly />
                <b-input v-else v-model="newPrediction.ship_to" placeholder="Destination code" />
              </b-field>
            </b-field>
          </section>
          <footer class="modal-card-foot">
            <button type="submit" class="button is-info is-fullwidth">
              Add to pending changes
            </button>
          </footer>
        </div>
      </form>
    </b-modal>

    <b-modal
      :key="colModalKey"
      :active.sync="isCheckingColls"
      scroll="keep"
      has-modal-card
    >
      <div v-if="isCheckingColls" style="margin-left: -20vw;">
        <div class="modal-card" style="min-width: 70vw; width: fit-content; min-height: 50vh; height: fit-content;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Movements
              <span v-if="storedName" style="font-size:1rem; text-overflow: elipsis">{{ storedName }}</span>
              <span v-if="displayedColls.collDate" class="ml-3" style="font-size:1rem; text-overflow: elipsis">{{ displayedColls.collDate }}</span>
            </p>
          </header>
          <section class="modal-card-body">
            <div style="display: flex; align-items: center; margin-top: -20px; margin-bottom: 20px">
              View Crates OUT
              <b-switch v-model="collsInDisplayed" style="margin-left: 10px;" />
              View Crates IN
            </div>
            <div v-if="collsInDisplayed">
              <div class="columns" style="background-color: rgb(255, 255, 255);">
                <div class="column">
                  Origin
                </div>
                <div class="column">
                  OB
                </div>
                <div class="column">
                  RO
                </div>
                <div class="column">
                  Quantity
                </div>
                <div class="column">
                  Type
                </div>
                <div class="column">
                  Status
                </div>
                <div class="column">
                  Date
                </div>
              </div>
              <div
                v-for="doneCollIn in displayedColls.doneCollsIn"
                :key="doneCollIn.id"
                class="columns"
                :style="{
                  'color': doneCollIn.relic ? 'blue' : 'black',
                  'font-weight': doneCollIn.naval ? 'bold' : 'regular',
                  'background-color': 'rgb(162, 200, 215)',
                  'border-top': 'solid 1px rgb(220, 220, 220)',
                  'border-left': 'solid 1px rgb(220, 220, 220)',
                  'border-right': 'solid 1px rgb(220, 220, 220)'
                }"
              >
                <div class="shipping-detail">
                  <b-tooltip :label="doneCollIn.name" position="is-right">
                    <div style="overflow: hidden; text-overflow: ellipsis; text-wrap: nowrap; width: 18ch;">
                      {{ doneCollIn.name }}
                    </div>
                  </b-tooltip>
                </div>
                <div class="shipping-detail">
                  {{ displayedOb(doneCollIn) }}
                </div>
                <div class="shipping-detail">
                  {{ doneCollIn.shipment }}
                </div>
                <div class="shipping-detail">
                  {{ parseInt(doneCollIn.num_crates) }}
                </div>
                <div class="shipping-detail">
                  {{ doneCollIn.type }}
                </div>
                <div class="shipping-detail">
                  {{ doneCollIn.status }}
                </div>
                <div class="shipping-detail">
                  {{ doneCollIn.done_date }}
                </div>
              </div>
              <div
                v-for="predCollIn in displayedColls.predCollsIn"
                :key="predCollIn.id"
                class="columns"
                :style="{
                  'color': predCollIn.relic ? 'blue' : 'black',
                  'font-weight': predCollIn.naval ? 'bold' : 'regular',
                  'background-color': 'rgb(162, 200, 215)',
                  'border-top': 'solid 1px rgb(220, 220, 220)',
                  'border-left': 'solid 1px rgb(220, 220, 220)',
                  'border-right': 'solid 1px rgb(220, 220, 220)'
                }"
              >
                <div class="shipping-detail">
                  <b-tooltip :label="predCollIn.name" position="is-right">
                    <div style="overflow: hidden; text-overflow: ellipsis; text-wrap: nowrap; width: 18ch;">
                      {{ predCollIn.name }}
                    </div>
                  </b-tooltip>
                </div>
                <div class="shipping-detail">
                  {{ displayedOb(predCollIn) }}
                </div>
                <div class="shipping-detail">
                  {{ predCollIn.shipment }}
                </div>
                <div class="shipping-detail">
                  {{ parseInt(predCollIn.num_crates) }}
                </div>
                <div class="shipping-detail">
                  {{ predCollIn.type }}
                </div>
                <div class="shipping-detail">
                  {{ predCollIn.status }}
                </div>
                <div class="shipping-detail">
                  {{ predCollIn.originDate }}
                </div>
              </div>
            </div>
            <div v-if="!collsInDisplayed">
              <div class="columns" style="background-color: rgb(255, 255, 255);">
                <div class="column">
                  Destination
                </div>
                <div class="column">
                  OB
                </div>
                <div class="column">
                  RO
                </div>
                <div class="column">
                  Quantity
                </div>
                <div class="column">
                  Type
                </div>
                <div class="column">
                  Status
                </div>
                <div class="column">
                  Date
                </div>
              </div>
              <div
                v-for="doneCollOut in displayedColls.doneCollsOut"
                :key="doneCollOut.id"
                class="columns"
                :style="{
                  'color': doneCollOut.relic ? 'blue' : 'black',
                  'font-weight': doneCollOut.naval ? 'bold' : 'regular',
                  'background-color': doneCollOut.shipType === 'DEL'? 'rgb(146, 208, 80)': 'rgb(251, 226, 213)',
                  'border-top': 'solid 1px rgb(220, 220, 220)',
                  'border-left': 'solid 1px rgb(220, 220, 220)',
                  'border-right': 'solid 1px rgb(220, 220, 220)'
                }"
              >
                <div class="shipping-detail">
                  <b-tooltip :label="doneCollOut.name" position="is-right">
                    <div style="overflow: hidden; text-overflow: ellipsis; text-wrap: nowrap; width: 18ch;">
                      {{ doneCollOut.name }}
                    </div>
                  </b-tooltip>
                </div>
                <div class="shipping-detail">
                  {{ displayedOb(doneCollOut) }}
                </div>
                <div class="shipping-detail">
                  {{ doneCollOut.shipment }}
                </div>
                <div class="shipping-detail">
                  {{ parseInt(doneCollOut.num_crates) }}
                </div>
                <div class="shipping-detail">
                  {{ doneCollOut.type }}
                </div>
                <div class="shipping-detail">
                  {{ doneCollOut.status }}
                </div>
                <div class="shipping-detail">
                  {{ doneCollOut.done_date }}
                </div>
              </div>
              <div
                v-for="predCollOut in displayedColls.predCollsOut"
                :key="predCollOut.id"
                class="columns"
                :style="{
                  'color': predCollOut.relic ? 'blue' : 'black',
                  'font-weight': predCollOut.naval ? 'bold' : 'regular',
                  'background-color': predCollOut.shipType === 'DEL'? 'rgb(146, 208, 80)': 'rgb(251, 226, 213)',
                  'border-top': 'solid 1px rgb(220, 220, 220)',
                  'border-left': 'solid 1px rgb(220, 220, 220)',
                  'border-right': 'solid 1px rgb(220, 220, 220)'
                }"
              >
                <div class="shipping-detail">
                  <b-tooltip :label="predCollOut.name" position="is-right">
                    <div style="overflow: hidden; text-overflow: ellipsis; text-wrap: nowrap; width: 18ch;">
                      {{ predCollOut.name }}
                    </div>
                  </b-tooltip>
                </div>
                <div class="shipping-detail">
                  {{ displayedOb(predCollOut) }}
                </div>
                <div class="shipping-detail">
                  {{ predCollOut.shipment }}
                </div>
                <div class="shipping-detail">
                  {{ parseInt(predCollOut.num_crates) }}
                </div>
                <div class="shipping-detail">
                  {{ predCollOut.type }}
                </div>
                <div class="shipping-detail">
                  {{ predCollOut.status }}
                </div>
                <div class="shipping-detail">
                  {{ predCollOut.originDate }}
                </div>
              </div>
            </div>
            <div v-if="displayedFalseShipments && displayedFalseShipments.length > 0">
              <div class="columns mt-4" style="background-color: white;">
                <div class="shipping-detail">
                  Name
                </div>
                <div class="shipping-detail">
                  Quantity
                </div>
              </div>
              <div
                v-for="(ship, index) in displayedFalseShipments"
                :key="index + '-displayedships'"
                class="columns"
                :style="{
                  'background-color': 'gainsboro',
                  'border-top': 'solid 1px rgb(255, 255, 255)',
                  'border-left': 'solid 1px rgb(255, 255, 255)',
                  'border-right': 'solid 1px rgb(255, 255, 255)'
                }"
              >
                <div class="shipping-detail">
                  {{ ship.name }}
                </div>
                <div class="shipping-detail">
                  {{ ship.qty }}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </b-modal>

    <b-modal :key="'commentModal'" :active.sync="isEditingComment" scroll="keep" has-modal-card>
      <form v-if="isEditingComment" @submit.prevent="submitComment(false)">
        <div class="modal-card" style="min-width: 50vw; width: fit-content; min-height: 50vh; height: fit-content;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Edit Comment
              <span v-if="storedName" style="font-size:1rem; text-overflow: elipsis">{{ storedName }}</span>
              <span v-if="editableComment.date" class="ml-3" style="font-size:1rem; text-overflow: elipsis">{{ editableComment.date }}</span>
            </p>
          </header>
          <section class="modal-card-body">
            <b-field label="Comment">
              <b-input v-model="bufferText" type="textarea" />
            </b-field>
            <b-button
              class="left-right"
              type="is-danger"
              icon-left="box"
              @click="() => {submitComment(true)}"
            >
              Delete
            </b-button>
          </section>
          <footer class="modal-card-foot">
            <button type="submit" class="button is-info is-fullwidth">
              {{ $t('global.submit') }}
            </button>
          </footer>
        </div>
      </form>
    </b-modal>

    <b-modal :key="modalKey" :active.sync="isEditingForecast" scroll="keep" has-modal-card>
      <form v-if="isEditingForecast" novalidate @submit.prevent="submitEdit">
        <div class="modal-card" style="min-width: 50vw; width: fit-content; min-height: 50vh; height: fit-content;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Edit performance forecast
              <span v-if="storedName" style="font-size:1rem; text-overflow: elipsis">{{ storedName }}</span>
              <span v-if="editableForecast.date" class="ml-3" style="font-size:1rem; text-overflow: elipsis">{{ editableForecast.date }}</span>
            </p>
          </header>
          <section class="modal-card-body">
            <b-field grouped>
              <b-field label="Start Date">
                <b-datepicker
                  v-model="editDateStart"
                  name="editDateStart"
                  :first-day-of-week="1"
                  :locale="'en-CA'"
                  icon="calendar-alt"
                  placeholder="Start Date"
                />
              </b-field>
              <b-field v-if="editBatch" label="End Date">
                <b-datepicker
                  v-model="editDateEnd"
                  name="editDateEnd"
                  :first-day-of-week="1"
                  :locale="'en-CA'"
                  icon="calendar-alt"
                  placeholder="End Date"
                />
              </b-field>
              <b-field label="Multiple Days?">
                <b-checkbox v-model="editBatch" name="batchEdit" />
              </b-field>
            </b-field>
            <div class="columns">
              <div
                v-for="type in editableForecast.types"
                :key="type"
                class="column"
              >
                <div>{{ type }}</div>
                <div
                  v-for="status in editableForecast.statuses"
                  :key="status"
                >
                  {{ status }}
                  <span :style=" editableForecast.values[type][status].changed ? {color: 'blue'} : {}">
                    {{ editableForecast.values[type][status].num_crates }}
                  </span>
                </div>
                <b-field v-if="simAllowedForType(productionSimOperation(type), type)" :label="isClientType(type) ? 'Prepared' : 'Washed'" label-position="on-border" class="mt-1">
                  <b-input v-model="editableWashed[type]" :disabled="editableWashed[type] === null" type="number" step="1" />
                  <b-button icon-left="circle-xmark" type="is-warning" class="is-small" @click="() => {editableWashed[type] = null; modalKey += 1}" />
                </b-field>
                <b-field v-if="simAllowedForType('repair', type)" label="Repaired" label-position="on-border" class="mt-1">
                  <b-input v-model="editableRepaired[type]" :disabled="editableRepaired[type] === null" type="number" step="1" />
                  <b-button icon-left="circle-xmark" type="is-warning" class="is-small" @click="() => {editableRepaired[type] = null; modalKey += 1}" />
                </b-field>
                <b-field v-if="simAllowedForType(declassifyOperation, type)" label="Declassified" label-position="on-border" class="mt-1">
                  <b-input v-model="editableDeclassified[type]" :disabled="editableDeclassified[type] === null" type="number" step="1" />
                  <b-button icon-left="circle-xmark" type="is-warning" class="is-small" @click="() => {editableDeclassified[type] = null; modalKey += 1}" />
                </b-field>
                <b-field v-if="simAllowedForType('in__dirty', type)" label="In Dirty" label-position="on-border" class="mt-1">
                  <b-input v-model="editableInDirty[type]" :disabled="editableInDirty[type] === null" type="number" step="1" />
                  <b-button icon-left="circle-xmark" type="is-warning" class="is-small" @click="() => {editableInDirty[type] = null; modalKey += 1}" />
                </b-field>
                <b-field v-if="simAllowedForType('in__repair', type)" label="In Repair" label-position="on-border" class="mt-1">
                  <b-input v-model="editableInRepair[type]" :disabled="editableInRepair[type] === null" type="number" step="1" />
                  <b-button icon-left="circle-xmark" type="is-warning" class="is-small" @click="() => {editableInRepair[type] = null; modalKey += 1}" />
                </b-field>
                <b-field v-if="simAllowedForType('in__clean', type)" label="In Clean" label-position="on-border" class="mt-1">
                  <b-input v-model="editableInClean[type]" :disabled="editableInClean[type] === null" type="number" step="1" />
                  <b-button icon-left="circle-xmark" type="is-warning" class="is-small" @click="() => {editableInClean[type] = null; modalKey += 1}" />
                </b-field>
                <b-field v-if="simAllowedForType('out__dirty', type)" label="Out Dirty" label-position="on-border" class="mt-1">
                  <b-input v-model="editableOutDirty[type]" :disabled="editableOutDirty[type] === null" type="number" step="1" />
                  <b-button icon-left="circle-xmark" type="is-warning" class="is-small" @click="() => {editableOutDirty[type] = null; modalKey += 1}" />
                </b-field>
                <b-field v-if="simAllowedForType('out__repair', type)" label="Out Repair" label-position="on-border" class="mt-1">
                  <b-input v-model="editableOutRepair[type]" :disabled="editableOutRepair[type] === null" type="number" step="1" />
                  <b-button icon-left="circle-xmark" type="is-warning" class="is-small" @click="() => {editableOutRepair[type] = null; modalKey += 1}" />
                </b-field>
                <b-field v-if="simAllowedForType('out__clean', type)" label="Out Clean" label-position="on-border" class="mt-1">
                  <b-input v-model="editableOutClean[type]" :disabled="editableOutClean[type] === null" type="number" step="1" />
                  <b-button icon-left="circle-xmark" type="is-warning" class="is-small" @click="() => {editableOutClean[type] = null; modalKey += 1}" />
                </b-field>
                <b-field v-if="simAllowedForType('forklift__cleanRejectedDirty', type)" label="Rej Clean>Dirty" label-position="on-border" class="mt-1">
                  <b-input v-model="editableRejCleanDirty[type]" :disabled="editableRejCleanDirty[type] === null" type="number" step="1" />
                  <b-button icon-left="circle-xmark" type="is-warning" class="is-small" @click="() => {editableRejCleanDirty[type] = null; modalKey += 1}" />
                </b-field>
                <b-field v-if="simAllowedForType('forklift__cleanRejectedRepair', type)" label="Rej Clean>Repair" label-position="on-border" class="mt-1">
                  <b-input v-model="editableRejCleanRepair[type]" :disabled="editableRejCleanRepair[type] === null" type="number" step="1" />
                  <b-button icon-left="circle-xmark" type="is-warning" class="is-small" @click="() => {editableRejCleanRepair[type] = null; modalKey += 1}" />
                </b-field>
                <b-field v-if="simAllowedForType('forklift__dirtyRejectedRepair', type)" label="Rej Dirty>Repair" label-position="on-border" class="mt-1">
                  <b-input v-model="editableRejDirtyRepair[type]" :disabled="editableRejDirtyRepair[type] === null" type="number" step="1" />
                  <b-button icon-left="circle-xmark" type="is-warning" class="is-small" @click="() => {editableRejDirtyRepair[type] = null; modalKey += 1}" />
                </b-field>
              </div>
            </div>
          </section>
          <footer class="modal-card-foot">
            <button type="submit" class="button is-info is-fullwidth" :class="{ 'is-loading': isSaving }" :disabled="isSaving">
              {{ $t('global.submit') }}
            </button>
          </footer>
        </div>
      </form>
    </b-modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Forecast from '../../services/v3/crateForecast'
import exceljs from 'exceljs'
import { saveAs } from 'file-saver'
import plantSelector from '@/mixins/plantSelector'
import saving from '@/mixins/saving'
import { restoreConsultingPlant } from '@/helpers/plants'
import { restoreMeanFlags, saveMeanFlags, restoreHiddenTypes, saveHiddenTypes } from '@/helpers/forecastPreferences'
import { SPECIAL_CLIENT_WASH, SPECIAL_CLIENT_DECLASSIFY } from '@/helpers/movementOperations'

// The four things a client type's crates actually do: prepared out of the root's
// dirty stock, declassified back into it, and shipped in and out clean. Washing,
// repair and the reject transitions would all debit a cell it can never fill.
const CLIENT_TYPE_SIM_OPERATIONS = ['in__clean', 'out__clean', SPECIAL_CLIENT_WASH, SPECIAL_CLIENT_DECLASSIFY]
// Column letters run out at Z, and the export addresses `types.length + 10`. Past
// that, exceljs writes to a garbage address or throws part-way through, and a
// corrupt workbook is worse than none.
const MAX_EXCEL_TYPES = 15

// How far down the page the control block folds into its one-line summary. Anything
// shorter than this and the first forecast card is still under the fold anyway.
const TOOLBAR_CONDENSE_SCROLL = 120

// The two landmarks on a board that can run for months: the day being lived, and the
// last day the site actually moved crates -- everything after it is prediction.
const TODAY_ACCENT = 'rgb(50, 115, 220)'
const LAST_PRODUCTION_ACCENT = 'rgb(17, 184, 0)'
// A day whose forklift declaration was rejected on one side: SAP stands in for every
// crate type there, so the whole day reads differently from what the site declared.
const FORKLIFT_DISCREPANCY_ACCENT = 'rgb(220, 30, 30)'

export default {
  name: 'CrateForecast',
  components: {
  },
  mixins: [plantSelector, saving],
  data: function () {
    return {
      dateStart: JSON.parse(sessionStorage.getItem('dateStart')) ? new Date(JSON.parse(sessionStorage.getItem('dateStart'))): new Date(),
      dateEnd: JSON.parse(sessionStorage.getItem('dateEnd')) ? new Date(JSON.parse(sessionStorage.getItem('dateEnd'))): new Date(),
      isLoading: false,
      isToolbarCondensed: false,
      // Where the page was when the user last worked the toggle, so a deliberate
      // choice survives every scroll event until they leave that part of the page.
      toolbarOverrideRegion: null,
      toolbarScrollFrame: null,
      toolbarObserver: null,
      toolbarHeight: 0,
      toolbarFullHeight: 0,
      holidays: [],
      modalKey: 0,
      colModalKey: -1,
      activeTab: '5.2',
      selectedPlant: restoreConsultingPlant(),
      storedPlantId: null,
      storedName: null,
      shipmentView: false,
      editDate: null,
      siteTypes: [],
      selectedTypes: [],
      forecasts: [],
      simulations: [],
      crateForecasts: [],
      crateMeans: [],
      collMeans: [],
      target: [],
      inventories: [],
      falseShipments: [],
      falseStart: [],
      falseEnd: [],
      displayedFalseShipments: [],
      washingDays: [],
      // Display order of the crate stocks, on the day cards, the edit modal and the
      // xlsx export alike. The API returns them clean first (see the forecast route).
      statusOrder: ['DIRTY', 'REPAIR', 'CLEAN'],
      crateIOs: [],
      // The site's client-specific crate types, as returned with the forecast. These
      // are prepared crates SAP ships under their root material, recognised by the
      // provider/destination pairing rather than by anything on the shipment.
      clientCrateTypes: [],
      // 'date__in|out' -> the override row, for each side of a day whose declared total
      // sat too far from SAP's to trust. SAP was used for every type on that side, so
      // the whole day needs flagging.
      forkliftDiscrepancies: {},
      bookings: [],
      collectMeanCounts: true,
      washingMeanCounts: true,
      repairMeanCounts: true,
      rejectMeanCounts: false,
      deliveryMeanCounts: false,
      // The mean flags the forecast on screen was fetched with.
      loadedMeanFlags: null,
      // Set while the saved flags of a newly picked site are being applied, so that
      // restoring a site's choice is not itself saved back as one.
      isRestoringMeanFlags: false,
      isEditingForecast: false,
      isEditingComment: false,
      isCheckingColls: false,
      isCheckingUndecided: false,
      isResetting: false,
      resetTableType: 'simulations',
      selectedResetRows: [],
      resetSortDesc: true,
      // Type and operation do not apply to fake shipments, which carry neither.
      resetFilters: { date: null, crates: '', type: '', operation: '' },
      // Which header filters show their input; closing one also clears it.
      resetFilterOpen: { date: false, type: false, operation: false, crates: false },
      collsInDisplayed: true,
      hideOldUndecided: false,
      editableForecast: [],
      editableComment: [],
      bufferText: null,
      displayedColls: [],
      undecidedCollsIn: [],
      undecidedCollsOut: [],
      totalUndecidedIn: 0,
      totalUndecidedOut: 0,
      originalWashed: [],
      editableWashed: [],
      originalRepaired: [],
      editableRepaired: [],
      // Declassification: a prepared crate that got soiled going back to its root
      // type's dirty stock. Only client-specific types have it.
      originalDeclassified: [],
      editableDeclassified: [],
      originalInDirty: [],
      editableInDirty: [],
      originalInRepair: [],
      editableInRepair: [],
      originalInClean: [],
      editableInClean: [],
      originalOutDirty: [],
      editableOutDirty: [],
      originalOutRepair: [],
      editableOutRepair: [],
      originalOutClean: [],
      editableOutClean: [],
      originalRejCleanDirty: [],
      editableRejCleanDirty: [],
      originalRejCleanRepair: [],
      editableRejCleanRepair: [],
      originalRejDirtyRepair: [],
      editableRejDirtyRepair: [],
      editDateStart: null,
      editDateEnd: null,
      editBatch: false,
      pinnedData: [],
      file: null,
      fileBuffer: null,
      days: [],
      isImportingExcel: false,
      loadedExcel: false,
      rawSimData: [],
      lastDate: '',
      weeklyMeanSimorep: 0,
      // --- moved shipments (drag & drop + new predictions) ---
      collects: [], // working copy of forecast collects, mutated optimistically on move/create
      movedShipments: [], // raw saved move/prediction records for the site (from the forecast payload)
      simData: [], // kept so the shipment view can be rebuilt after an optimistic move
      draggingOb: null, // OB (collect id) currently being dragged
      dragOverDate: null, // day card currently hovered while dragging
      dragPointerY: 0, // last pointer Y during a drag, for edge auto-scroll
      dragScrollRaf: null, // requestAnimationFrame handle for edge auto-scroll
      pendingNewCounter: 0,
      isSubmittingMoves: false,
      isAddingPrediction: false,
      predictionDate: null,
      newPrediction: {
        shipment_type: 'COL',
        ship_from: '',
        ship_to: '',
        crate_type: '5.2',
        crate_status: 'DIRTY',
        num_crates: null
      }
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user
    }),
    clientTypeNames () {
      return this.clientCrateTypes.map(x => x.name)
    },
    // Exposed for the edit modal, which cannot read a module constant directly.
    declassifyOperation () {
      return SPECIAL_CLIENT_DECLASSIFY
    },
    // The xlsx layout puts one column per crate type and addresses the blocks to
    // their right by list length, so a wide enough site runs off the end of the
    // alphabet. Sites cap out well below this, but a corrupt export is bad enough to
    // be worth refusing outright.
    tooManyTypesForExcel () {
      return (this.target.types ? this.target.types.length : 0) > MAX_EXCEL_TYPES
    },
    excelDisabledReason () {
      return this.tooManyTypesForExcel
        ? `This site tracks ${this.target.types.length} crate types; the spreadsheet layout supports ${MAX_EXCEL_TYPES}.`
        : null
    },
    activeMeanCount () {
      return [this.collectMeanCounts, this.washingMeanCounts, this.repairMeanCounts, this.rejectMeanCounts]
        .filter(Boolean).length
    },
    // The Means dropdown as the request takes it, and as it is remembered per site.
    meanFlags () {
      return {
        collect: this.collectMeanCounts,
        washing: this.washingMeanCounts,
        repair: this.repairMeanCounts,
        reject: this.rejectMeanCounts,
        delivery: this.deliveryMeanCounts
      }
    },
    meanFlagSignature () {
      return [
        this.collectMeanCounts,
        this.washingMeanCounts,
        this.repairMeanCounts,
        this.rejectMeanCounts,
        this.deliveryMeanCounts
      ].join('|')
    },
    // The flags are request parameters, so a forecast already on screen was fetched
    // with whatever was set at the time. Without this the page just looks like it
    // ignored the change.
    meansNeedReload () {
      return this.forecasts.length > 0 &&
        this.loadedMeanFlags !== null &&
        this.loadedMeanFlags !== this.meanFlagSignature
    },
    // Blank space standing in for the rows that fold away, so the forecast below the
    // bar holds still while the bar itself shrinks.
    toolbarSpacerHeight () {
      return Math.max(0, this.toolbarFullHeight - this.toolbarHeight)
    },
    // One line has no room for two date pickers, so the loaded range is spelled out.
    toolbarDateRange () {
      const day = (d) => d ? new Date(d).toLocaleDateString('en-CA') : '—'
      return `${day(this.dateStart)} → ${day(this.dateEnd)}`
    },
    todayAccent () {
      return TODAY_ACCENT
    },
    lastProductionAccent () {
      return LAST_PRODUCTION_ACCENT
    },
    forkliftDiscrepancyAccent () {
      return FORKLIFT_DISCREPANCY_ACCENT
    },
    weekAgoStr () {
      const d = new Date()
      d.setDate(d.getDate() - 7)
      return d.toLocaleDateString('en-CA')
    },
    recentUndecidedCollsIn () {
      return this.undecidedCollsIn.filter(x => x.originDate === null || x.originDate >= this.weekAgoStr)
    },
    recentUndecidedCollsOut () {
      return this.undecidedCollsOut.filter(x => x.originDate === null || x.originDate >= this.weekAgoStr)
    },
    displayedUndecidedCollsIn () {
      return this.hideOldUndecided ? this.recentUndecidedCollsIn : this.undecidedCollsIn
    },
    displayedUndecidedCollsOut () {
      return this.hideOldUndecided ? this.recentUndecidedCollsOut : this.undecidedCollsOut
    },
    totalRecentUndecidedIn () {
      return this.recentUndecidedCollsIn.reduce((sum, element) => sum + parseInt(element.num_crates), 0)
    },
    totalRecentUndecidedOut () {
      return this.recentUndecidedCollsOut.reduce((sum, element) => sum + parseInt(element.num_crates), 0)
    },
    // Readable counterparty name per OB, taken from the SAP rows the page already
    // loaded (SHIPTO_NAME / SHIPFROM_NAME, the name the day cards show). A
    // moved_shipment record itself only stores the ship_from/ship_to codes, so a
    // move borrows the name of the shipment it overrides. Keyed by OB + shipType
    // first, mirroring the server's guard against a reused OB matching both legs.
    collectNamesByOb () {
      const byOb = {}
      this.collects.forEach((c) => {
        if (c.id == null || !c.name) return
        byOb[c.id + '__' + c.shipType] = c.name
        if (!byOb[c.id]) byOb[c.id] = c.name
      })
      return byOb
    },
    // The OB users know (SAP DELIV_ID) per crate id, for the moved_shipment records,
    // which only store the id. Keyed like collectNamesByOb.
    displayedObById () {
      const byId = {}
      this.collects.forEach((c) => {
        if (c.id == null || !c.displayed_ob) return
        byId[c.id + '__' + c.shipType] = c.displayed_ob
        if (!byId[c.id]) byId[c.id] = c.displayed_ob
      })
      return byId
    },
    resetSourceRows () {
      if (this.resetTableType === 'movedShipments') return this.movedShipments
      return this.resetTableType === 'falseShipments' ? this.falseShipments : this.rawSimData
    },
    // Filter choices come from the unfiltered table, so picking one never empties
    // the list of the other.
    resetTypeOptions () {
      return [...new Set(this.resetSourceRows.map(this.resetRowType).filter(Boolean))].sort()
    },
    resetOperationOptions () {
      return [...new Set(this.resetSourceRows.map(this.resetRowOperation).filter(Boolean))].sort()
    },
    resetFiltersActive () {
      const f = this.resetFilters
      return f.date !== null || f.crates !== '' || f.type !== '' || f.operation !== ''
    },
    resetTableRows () {
      const f = this.resetFilters
      const date = f.date ? f.date.toLocaleDateString('en-CA') : null
      const crates = f.crates === '' || f.crates === null ? null : parseInt(f.crates)
      const rows = this.resetSourceRows.filter(row =>
        (date === null || this.resetRowDate(row) === date) &&
        (crates === null || isNaN(crates) || this.resetRowCrates(row) === crates) &&
        (f.type === '' || this.resetRowType(row) === f.type) &&
        (f.operation === '' || this.resetRowOperation(row) === f.operation)
      )
      const dateKey = this.resetTableType === 'movedShipments' ? 'moved_date' : 'date'
      const dir = this.resetSortDesc ? -1 : 1
      return rows.sort((a, b) => dir * (new Date(a[dateKey]) - new Date(b[dateKey])))
    },
    // Number of unsaved shipment changes staged this session: OBs relocated to a new
    // day + brand-new predictions not yet persisted. Drives the pending-changes bar.
    predictionIsIn () {
      return ['COL', 'TRFI', 'NCR'].includes(this.newPrediction.shipment_type)
    },
    pendingCount () {
      const movedObs = new Set()
      let creates = 0
      this.collects.forEach((c) => {
        if (c._new) {
          creates += 1
        } else if (this.normDate(c.date) !== this.normDate(c._baseDate)) {
          movedObs.add(c.id)
        }
      })
      return movedObs.size + creates
    },
    allResetRowsSelected: {
      get () {
        return this.resetTableRows.length > 0 && this.selectedResetRows.length === this.resetTableRows.length
      },
      set (val) {
        this.selectedResetRows = val ? this.resetTableRows.map(row => row.id) : []
      }
    }
  },
  watch: {
    // A row hidden by a filter must not stay selected, or Archive Selected would
    // archive rows the user can no longer see.
    resetTableRows (rows) {
      const visible = new Set(rows.map(row => row.id))
      const kept = this.selectedResetRows.filter(id => visible.has(id))
      if (kept.length !== this.selectedResetRows.length) this.selectedResetRows = kept
    },
    // Picking a client type in the prediction modal takes DIRTY and REPAIR off the
    // status list, so a status chosen before the switch has to be corrected or the
    // modal would submit one that is no longer offered.
    'newPrediction.crate_type' (type) {
      if (this.isClientType(type) && this.newPrediction.crate_status !== 'CLEAN') {
        this.newPrediction.crate_status = 'CLEAN'
      }
    },
    selectedPlant (plant) {
      this.applySavedMeanFlags(plant)
    },
    // Saved against the site as they are ticked rather than on Load, so a user who
    // sets the means up and walks away still finds them next time.
    meanFlagSignature () {
      if (this.isRestoringMeanFlags) {
        return
      }
      saveMeanFlags(this.selectedPlant?.id, this.meanFlags)
    }
  },
  mounted: async function () {
    window.addEventListener('scroll', this.onPageScroll, { passive: true })
    window.addEventListener('resize', this.syncToolbarMetrics)
    // The bar grows and shrinks on its own as plants, crate types and stock cards
    // load, so its size is watched rather than measured at a few chosen moments.
    if (window.ResizeObserver) {
      this.toolbarObserver = new window.ResizeObserver(() => this.syncToolbarMetrics())
      this.toolbarObserver.observe(this.$refs.toolbar)
    }
    this.syncToolbarMetrics()
    this.onPageScroll()
    // The plant comes back from sessionStorage in data(), too early for the watcher.
    this.applySavedMeanFlags(this.selectedPlant)
    await this.loadPlants()
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.onPageScroll)
    window.removeEventListener('resize', this.syncToolbarMetrics)
    if (this.toolbarObserver) this.toolbarObserver.disconnect()
    if (this.toolbarScrollFrame) window.cancelAnimationFrame(this.toolbarScrollFrame)
  },
  methods: {
    // A site nothing was ever saved for keeps whatever is ticked, which is how the
    // page behaved before anything was remembered -- it is only the sites the user
    // has actually set up that pull their own choice back.
    applySavedMeanFlags (plant) {
      const saved = restoreMeanFlags(plant?.id)
      if (!saved) {
        return
      }
      this.isRestoringMeanFlags = true
      if (saved.collect !== undefined) this.collectMeanCounts = saved.collect
      if (saved.washing !== undefined) this.washingMeanCounts = saved.washing
      if (saved.repair !== undefined) this.repairMeanCounts = saved.repair
      if (saved.reject !== undefined) this.rejectMeanCounts = saved.reject
      if (saved.delivery !== undefined) this.deliveryMeanCounts = saved.delivery
      this.$nextTick(() => { this.isRestoringMeanFlags = false })
    },
    // The Types picker is a display filter -- every type is still forecast, and
    // crateIOs is built from the site's full list -- so this only decides which
    // columns the cards draw.
    visibleSiteTypes () {
      const hidden = restoreHiddenTypes(this.selectedPlant?.id)
      const shown = hidden ? this.siteTypes.filter(type => !hidden.includes(type.name)) : this.siteTypes.slice()
      // A board with no columns reads as a load that failed rather than as a filter,
      // so a stale choice that hides everything the site now has falls back to all.
      return shown.length > 0 ? shown : this.siteTypes.slice()
    },
    // Only ever called from the picker itself, so the assignments a load makes are
    // never mistaken for a choice.
    persistHiddenTypes () {
      const shown = this.selectedTypes.map(type => type.name)
      saveHiddenTypes(this.selectedPlant?.id, this.siteTypes.map(type => type.name).filter(name => !shown.includes(name)))
    },
    resetData () {
      this.forecasts = []
      this.crateForecasts = []
      // Belongs to the forecast on screen: a load that fails before it is recomputed
      // must not leave the Last Production marker on a day from the previous site.
      this.lastDate = ''
      this.crateMeans = []
      this.collMeans = []
      this.falseShipments = []
      this.displayedFalseShipments = []
      this.crateIOs = []
      this.forkliftDiscrepancies = {}
      this.simulations = []
      this.editableForecast = []
      this.editableComment = []
      this.bufferText = null
      this.displayedColls = []
      this.undecidedCollsIn = []
      this.undecidedCollsOut = []
      this.totalUndecidedIn = 0
      this.totalUndecidedOut = 0
      this.originalWashed = []
      this.target = []
      this.inventories = []
      this.washingDays = []
      this.editableWashed = []
      this.originalRepaired = []
      this.editableRepaired = []
      this.originalDeclassified = []
      this.editableDeclassified = []
      this.selectedTypes = []
      this.originalInDirty = []
      this.editableInDirty = []
      this.originalInRepair = []
      this.editableInRepair = []
      this.originalInClean = []
      this.editableInClean = []
      this.originalOutDirty = []
      this.editableOutDirty = []
      this.originalOutRepair = []
      this.editableOutRepair = []
      this.originalOutClean = []
      this.editableOutClean = []
      this.originalRejCleanDirty = []
      this.editableRejCleanDirty = []
      this.originalRejCleanRepair = []
      this.editableRejCleanRepair = []
      this.originalRejDirtyRepair = []
      this.editableRejDirtyRepair = []
      this.editDateEnd = null
      this.editDateStart = null
      this.editBatch = false
      this.fileBuffer = null
      this.days = []
      this.newFalseShipments = []
      this.rawSimData = []
      this.collects = []
      this.movedShipments = []
      this.simData = []
      this.draggingOb = null
      this.dragOverDate = null
    },
    editForecast (forecast) {
      this.editableForecast = forecast
      this.editableForecast.types.forEach((type) => {
        let collVals = this.crateIOs[type].filter(x => {
          return new Date(x.collDate).toLocaleDateString('en-CA') === new Date(forecast.date).toLocaleDateString('en-CA')
        })
        // What a type produces, and the reverse flow out of that stock, are different
        // operations for a client type: preparation out of one of the root's stocks,
        // and declassification back into it. A client type tracks no mean, so an absent
        // simulation leaves the field at 0 rather than at a predicted figure.
        const prodOp = this.productionSimOperation(type)
        const prodSim = this.simulations[forecast.date + '__' + prodOp + '__' + type]
        const prodFallback = this.isClientType(type) ? 0 : this.crateMeans[type + '__' + 'washing']
        this.originalWashed[type] = prodSim || prodFallback
        this.editableWashed[type] = prodSim || prodFallback
        if (this.isClientType(type)) {
          const decSim = this.simulations[forecast.date + '__' + SPECIAL_CLIENT_DECLASSIFY + '__' + type]
          this.originalDeclassified[type] = decSim || 0
          this.editableDeclassified[type] = decSim || 0
          this.originalRepaired[type] = 0
          this.editableRepaired[type] = 0
        } else {
          this.originalRepaired[type] = this.simulations[forecast.date + '__' + 'repair' + '__' + type] ? this.simulations[forecast.date + '__' + 'repair' + '__' + type] : this.crateMeans[type + '__' + 'repair']
          this.editableRepaired[type] = this.simulations[forecast.date + '__' + 'repair' + '__' + type] ? this.simulations[forecast.date + '__' + 'repair' + '__' + type] : this.crateMeans[type + '__' + 'repair']
        }
        this.originalInDirty[type] = collVals[0].dirtyIn
        this.editableInDirty[type] = collVals[0].dirtyIn
        this.originalInRepair[type] = collVals[0].repairIn
        this.editableInRepair[type] = collVals[0].repairIn
        this.originalInClean[type] = collVals[0].cleanIn
        this.editableInClean[type] = collVals[0].cleanIn
        this.originalOutDirty[type] = collVals[0].outDirty
        this.editableOutDirty[type] = collVals[0].outDirty
        this.originalOutRepair[type] = collVals[0].outRepair
        this.editableOutRepair[type] = collVals[0].outRepair
        this.originalOutClean[type] = collVals[0].outClean
        this.editableOutClean[type] = collVals[0].outClean
        this.originalRejCleanDirty[type] = this.simulations[forecast.date + '__' + 'forklift__cleanRejectedDirty' + '__' + type] ? this.simulations[forecast.date + '__' + 'forklift__cleanRejectedDirty' + '__' + type] : this.crateMeans[type + '__reject__CLEAN__DIRTY']
        this.editableRejCleanDirty[type] = this.simulations[forecast.date + '__' + 'forklift__cleanRejectedDirty' + '__' + type] ? this.simulations[forecast.date + '__' + 'forklift__cleanRejectedDirty' + '__' + type] : this.crateMeans[type + '__reject__CLEAN__DIRTY']
        this.originalRejCleanRepair[type] = this.simulations[forecast.date + '__' + 'forklift__cleanRejectedRepair' + '__' + type] ? this.simulations[forecast.date + '__' + 'forklift__cleanRejectedRepair' + '__' + type] : this.crateMeans[type + '__reject__CLEAN__REPAIR']
        this.editableRejCleanRepair[type] = this.simulations[forecast.date + '__' + 'forklift__cleanRejectedRepair' + '__' + type] ? this.simulations[forecast.date + '__' + 'forklift__cleanRejectedRepair' + '__' + type] : this.crateMeans[type + '__reject__CLEAN__REPAIR']
        this.originalRejDirtyRepair[type] = this.simulations[forecast.date + '__' + 'forklift__dirtyRejectedRepair' + '__' + type] ? this.simulations[forecast.date + '__' + 'forklift__dirtyRejectedRepair' + '__' + type] : this.crateMeans[type + '__reject__DIRTY__REPAIR']
        this.editableRejDirtyRepair[type] = this.simulations[forecast.date + '__' + 'forklift__dirtyRejectedRepair' + '__' + type] ? this.simulations[forecast.date + '__' + 'forklift__dirtyRejectedRepair' + '__' + type] : this.crateMeans[type + '__reject__DIRTY__REPAIR']
      })
      this.editDateStart = new Date(this.editableForecast.date)
      this.editDateEnd = null
      this.editBatch = false
      this.isEditingForecast = true
    },
    editComment (forecast) {
      this.editableComment = forecast
      if (this.editableComment.comment) {
        this.bufferText = this.editableComment.comment.text
      }
      this.isEditingComment = true
    },
    // Position of a stock status in `statusOrder`; an unexpected one sorts last.
    statusRank (status) {
      const rank = this.statusOrder.indexOf(status)
      return rank === -1 ? this.statusOrder.length : rank
    },
    isClientType (type) {
      return this.clientTypeNames.includes(type)
    },
    // The operation behind a type's "produced" figure: ordinary types wash crates,
    // client types prepare them out of their root type's stock.
    productionSimOperation (type) {
      return this.isClientType(type) ? SPECIAL_CLIENT_WASH : 'washing'
    },
    // The two production rows on a day card. An ordinary type washes crates and
    // repairs them; a client type prepares them out of its root type's stock and
    // declassifies them back into it. Both rows resolve the same way -- what was
    // declared, else what was simulated, else the mean -- but a client type tracks no
    // mean, so it stops at 0 rather than showing a prediction it cannot have.
    producedLabel (type) {
      return this.isClientType(type) ? 'Prepared' : 'Washed'
    },
    reverseLabel (type) {
      return this.isClientType(type) ? 'Declassified' : 'Repaired'
    },
    isToday (forecast) {
      return new Date(forecast.date).toLocaleDateString('en-CA') === new Date().toLocaleDateString('en-CA')
    },
    // `lastDate` is the newest loaded day carrying movements, which is what the xlsx
    // export has always labelled Last Production.
    isLastProduction (forecast) {
      return this.lastDate !== '' && new Date(forecast.date).toLocaleDateString('en-CA') === this.lastDate
    },
    // A rejected forklift declaration wins the frame over everything: it changes what
    // the whole day counted, for every type, and it is the one the site has to act on.
    // Today wins over last production when the site last produced today, since both
    // tags are on the card anyway and nothing is lost by it.
    dayAccent (forecast) {
      if (this.forkliftDiscrepancy(forecast.date, 'in') || this.forkliftDiscrepancy(forecast.date, 'out')) return FORKLIFT_DISCREPANCY_ACCENT
      if (this.isToday(forecast)) return TODAY_ACCENT
      return this.isLastProduction(forecast) ? LAST_PRODUCTION_ACCENT : null
    },
    // An ordinary day keeps the grey gradient frame; an inventory day and a holiday
    // keep their fill. Today and the last production day take a coloured frame on top
    // of all that -- the board runs for months and those two have to be findable.
    dayCardStyle (forecast) {
      const isHoliday = this.holidays.includes(new Date(forecast.date).toLocaleDateString('en-CA'))
      const accent = this.dayAccent(forecast)
      return {
        marginTop: '2rem',
        paddingRight: '0.75rem',
        backgroundColor: forecast.inventory ? 'rgb(252, 252, 149)' : isHoliday ? 'rgb(50, 50, 50)' : '',
        border: '2px solid ' + (accent || 'transparent'),
        borderImageSlice: '1',
        borderImage: accent ? 'none' : 'linear-gradient(to left, rgb(125, 125, 125), rgb(225, 225, 225)) 3',
        color: isHoliday ? 'rgb(255, 255, 255)' : 'black'
      }
    },
    // Means and production sims only speak for the days after the last declared
    // production: up to it, an undeclared figure is work that was not done, not work
    // not yet entered. testGet gates the stock on the same day.
    isPastLastProduction (forecast) {
      return new Date(forecast.date).toLocaleDateString('en-CA') > this.lastDate
    },
    // A mean fills a day only where testGet counts one: past the last production, on a
    // washing day that is not a holiday. Anywhere else the undeclared figure is 0.
    meanIsGated (forecast) {
      const day = new Date(forecast.date)
      return !this.isPastLastProduction(forecast) ||
        !this.washingDays.includes(day.getDay()) ||
        this.holidays.includes(day.toLocaleDateString('en-CA'))
    },
    producedSimulation (forecast, type) {
      return this.simulations[forecast.date + '__' + this.productionSimOperation(type) + '__' + type]
    },
    reverseSimulation (forecast, type) {
      const operation = this.isClientType(type) ? SPECIAL_CLIENT_DECLASSIFY : 'repair'
      return this.simulations[forecast.date + '__' + operation + '__' + type]
    },
    producedFigure (forecast, type) {
      if (forecast.washed[type] !== null) return forecast.washed[type]
      const sim = this.producedSimulation(forecast, type)
      if (sim) return sim
      if (this.isClientType(type) || this.meanIsGated(forecast)) return 0
      return this.crateMeans[type + '__' + 'washing']?.toFixed(1)
    },
    reverseFigure (forecast, type) {
      if (forecast.repaired[type]) return forecast.repaired[type]
      const sim = this.reverseSimulation(forecast, type)
      if (sim) return sim
      // A client type tracks no mean, so an undeclared, unsimulated day is simply 0.
      if (this.isClientType(type)) return 0
      return this.meanIsGated(forecast) ? 0 : this.crateMeans[type + '__' + 'repair']?.toFixed(1)
    },
    // Black for a declared figure, green for a simulated one, blue for a predicted one.
    producedColour (forecast, type) {
      if (forecast.washed[type]) return 'black'
      return this.producedSimulation(forecast, type) ? 'rgb(76, 160, 76)' : 'blue'
    },
    reverseColour (forecast, type) {
      if (forecast.repaired[type]) return 'black'
      return this.reverseSimulation(forecast, type) ? 'rgb(76, 160, 76)' : 'blue'
    },
    // Which of the eleven editable figures a type can actually carry. A client type
    // holds only prepared clean crates: a washing, repair or reject simulation on one
    // would take crates out of a dirty or repair stock it never has, and the API
    // discards them, so they are not offered here either.
    simAllowedForType (operation, type) {
      if (this.isClientType(type)) return CLIENT_TYPE_SIM_OPERATIONS.includes(operation)
      // The two client operations are meaningless on an ordinary type: it has no root
      // to prepare out of or declassify back into.
      return operation !== SPECIAL_CLIENT_WASH && operation !== SPECIAL_CLIENT_DECLASSIFY
    },
    // The override row for this side of this day, if the site's declared total failed
    // the 20% check against SAP's and SAP was used instead. The verdict is reached on
    // the day's totals over every crate type, because SAP files a mixed truck under a
    // single MATNR, so it covers every column at once.
    forkliftOverride (date, direction) {
      return this.forkliftDiscrepancies[new Date(date).toLocaleDateString('en-CA') + '__' + direction]
    },
    forkliftDiscrepancy (date, direction) {
      return !!this.forkliftOverride(date, direction)
    },
    // Tooltip spelling out what was compared: the two day totals, then how they split
    // per type, so the site can see which pallets to recount. A declaration SAP never
    // saw at all reads differently from one declared at the wrong figure.
    forkliftDiscrepancyTitle (date, direction) {
      const o = this.forkliftOverride(date, direction)
      if (!o) return ''
      const split = Object.keys(o.byType || {}).sort().map((type) => {
        return `${type}: declared ${o.byType[type].declared}, SAP ${o.byType[type].sap}`
      }).join('\n')
      const verdict = o.sap === 0
        ? `Forklift ${direction} of ${o.declared} declared across all types, but SAP counted none — SAP used for every type. Check it was not meant for the other direction.`
        : `Forklift ${direction} of ${o.declared} declared across all types is ${Math.round(Math.abs(o.declared - o.sap) / o.sap * 100)}% from SAP's ${o.sap} (it must be under 20%) — SAP used for every type.`
      return split ? verdict + '\n' + split : verdict
    },
    checkCollects (collects) {
      this.displayedColls = collects
      this.displayedFalseShipments = this.falseShipments.filter((x) => { return new Date(x.date).toLocaleDateString('en-CA') === collects.collDate })
      this.isCheckingColls = true
    },
    addHoliday (date) {
      Forecast.createHoliday({
        date: date,
        id_site: this.storedPlantId
      }).then(async (res) => {
        this.holidays = await Forecast.getHolidays({
          siteId: this.selectedPlant.id
        })
        this.holidays = this.holidays.map((x) => { return new Date(x.date).toLocaleDateString('en-CA') })
      })
    },
    removeHoliday (date) {
      Forecast.removeHoliday({
        date: date,
        id_site: this.storedPlantId
      }).then(async (res) => {
        this.holidays = await Forecast.getHolidays({
          siteId: this.selectedPlant.id
        })
        this.holidays = this.holidays.map((x) => { return new Date(x.date).toLocaleDateString('en-CA') })
      })
    },
    async loadFile (file) {
      this.file = file.target.files[0]
      const reader = new FileReader()

      reader.onload = (event) => {
        this.fileBuffer = event.target.result
      }
      await reader.readAsArrayBuffer(this.file)
      this.loadedExcel = true
    },
    chooseFiles: function () {
      document.getElementById('fileUpload').click()
    },
    async testExcel () {
      var readWb = new exceljs.Workbook()
      const excelCollumns = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
      await readWb.xlsx.load(this.fileBuffer)
      var display = readWb.getWorksheet('Crate Forecasts')
      var backup = readWb.getWorksheet('DONT TOUCH')
      var currentDate = display.getCell('A1').value
      this.falseStart = new Date(currentDate)
      var out = []
      var simFalseShipments = []
      var index = 0
      while (currentDate && currentDate.length > 0) {
        this.target.types.forEach((type, typeNum) => {
          // The column header names the type, which decides what rows 6 and 7 mean:
          // washing and repair for an ordinary type, preparation and declassification
          // for a client-specific one.
          const columnType = display.getCell(excelCollumns[typeNum + 1] + ((index * 17) + 2)).value
          let washed = display.getCell(excelCollumns[typeNum + 1] + (1 + (index * 17) + 5)).value
          let backupWashed = backup.getCell(excelCollumns[typeNum + 1] + (1 + (index * 17) + 5)).value
          if (washed !== backupWashed) {
            out.push({
              num_crates: parseInt(washed),
              operation: this.productionSimOperation(columnType),
              id_site: this.storedPlantId,
              date: currentDate,
              type: columnType
            })
          }
          let repaired = display.getCell(excelCollumns[typeNum + 1] + (1 + (index * 17) + 6)).value
          let backupRepaired = backup.getCell(excelCollumns[typeNum + 1] + (1 + (index * 17) + 6)).value
          if (repaired !== backupRepaired) {
            out.push({
              num_crates: parseInt(repaired),
              operation: this.isClientType(columnType) ? SPECIAL_CLIENT_DECLASSIFY : 'repair',
              id_site: this.storedPlantId,
              date: currentDate,
              type: columnType
            })
          }
          let inDirty = display.getCell(excelCollumns[typeNum + 1] + (1 + (index * 17) + 7)).value
          let backupInDirty = backup.getCell(excelCollumns[typeNum + 1] + (1 + (index * 17) + 7)).value
          if (inDirty !== backupInDirty) {
            out.push({
              num_crates: parseInt(inDirty),
              operation: 'in__dirty',
              id_site: this.storedPlantId,
              date: currentDate,
              type: display.getCell(excelCollumns[typeNum + 1] + ((index * 17) + 2)).value
            })
          }
          let inRepair = display.getCell(excelCollumns[typeNum + 1] + (1 + (index * 17) + 8)).value
          let backupInRepair = backup.getCell(excelCollumns[typeNum + 1] + (1 + (index * 17) + 8)).value
          if (inRepair !== backupInRepair) {
            out.push({
              num_crates: parseInt(inRepair),
              operation: 'in__repair',
              id_site: this.storedPlantId,
              date: currentDate,
              type: display.getCell(excelCollumns[typeNum + 1] + ((index * 17) + 2)).value
            })
          }
          let inClean = display.getCell(excelCollumns[typeNum + 1] + (1 + (index * 17) + 9)).value
          let backupInClean = backup.getCell(excelCollumns[typeNum + 1] + (1 + (index * 17) + 9)).value
          if (inClean !== backupInClean) {
            out.push({
              num_crates: parseInt(inClean),
              operation: 'in__clean',
              id_site: this.storedPlantId,
              date: currentDate,
              type: display.getCell(excelCollumns[typeNum + 1] + ((index * 17) + 2)).value
            })
          }
          let outDirty = display.getCell(excelCollumns[typeNum + 1] + (1 + (index * 17) + 10)).value
          let backupOutDirty = backup.getCell(excelCollumns[typeNum + 1] + (1 + (index * 17) + 10)).value
          if (outDirty !== backupOutDirty) {
            out.push({
              num_crates: parseInt(outDirty),
              operation: 'out__dirty',
              id_site: this.storedPlantId,
              date: currentDate,
              type: display.getCell(excelCollumns[typeNum + 1] + ((index * 17) + 2)).value
            })
          }
          let outRepair = display.getCell(excelCollumns[typeNum + 1] + (1 + (index * 17) + 11)).value
          let backupOutRepair = backup.getCell(excelCollumns[typeNum + 1] + (1 + (index * 17) + 11)).value
          if (outRepair !== backupOutRepair) {
            out.push({
              num_crates: parseInt(outRepair),
              operation: 'out__repair',
              id_site: this.storedPlantId,
              date: currentDate,
              type: display.getCell(excelCollumns[typeNum + 1] + ((index * 17) + 2)).value
            })
          }
          let outClean = display.getCell(excelCollumns[typeNum + 1] + (1 + (index * 17) + 12)).value
          let backupOutClean = backup.getCell(excelCollumns[typeNum + 1] + (1 + (index * 17) + 12)).value
          if (outClean !== backupOutClean) {
            out.push({
              num_crates: parseInt(outClean),
              operation: 'out__clean',
              id_site: this.storedPlantId,
              date: currentDate,
              type: display.getCell(excelCollumns[typeNum + 1] + ((index * 17) + 2)).value
            })
          }
          // Rejections. An edit to a collated reject cell becomes one simulation
          // under the canonical operation for that start→end transition.
          let rejCleanDirty = display.getCell(excelCollumns[typeNum + 1] + (1 + (index * 17) + 13)).value
          let backupRejCleanDirty = backup.getCell(excelCollumns[typeNum + 1] + (1 + (index * 17) + 13)).value
          if (rejCleanDirty !== backupRejCleanDirty) {
            out.push({
              num_crates: parseInt(rejCleanDirty),
              operation: 'forklift__cleanRejectedDirty',
              id_site: this.storedPlantId,
              date: currentDate,
              type: display.getCell(excelCollumns[typeNum + 1] + ((index * 17) + 2)).value
            })
          }
          let rejCleanRepair = display.getCell(excelCollumns[typeNum + 1] + (1 + (index * 17) + 14)).value
          let backupRejCleanRepair = backup.getCell(excelCollumns[typeNum + 1] + (1 + (index * 17) + 14)).value
          if (rejCleanRepair !== backupRejCleanRepair) {
            out.push({
              num_crates: parseInt(rejCleanRepair),
              operation: 'forklift__cleanRejectedRepair',
              id_site: this.storedPlantId,
              date: currentDate,
              type: display.getCell(excelCollumns[typeNum + 1] + ((index * 17) + 2)).value
            })
          }
          let rejDirtyRepair = display.getCell(excelCollumns[typeNum + 1] + (1 + (index * 17) + 15)).value
          let backupRejDirtyRepair = backup.getCell(excelCollumns[typeNum + 1] + (1 + (index * 17) + 15)).value
          if (rejDirtyRepair !== backupRejDirtyRepair) {
            out.push({
              num_crates: parseInt(rejDirtyRepair),
              operation: 'forklift__dirtyRejectedRepair',
              id_site: this.storedPlantId,
              date: currentDate,
              type: display.getCell(excelCollumns[typeNum + 1] + ((index * 17) + 2)).value
            })
          }
        })
        for (var loop = 0; loop < 10; loop += 1) {
          if (display.getCell(excelCollumns[this.target.types.length + 9] + (1 + (index * 17) + 2 + loop)).value !== null) {
            simFalseShipments.push({
              id_site: this.storedPlantId,
              date: currentDate,
              name: display.getCell(excelCollumns[this.target.types.length + 9] + (1 + (index * 17) + 2 + loop)).value,
              qty: display.getCell(excelCollumns[this.target.types.length + 10] + (1 + (index * 17) + 2 + loop)).value
            })
          }
        }
        index += 1
        this.falseEnd = new Date(currentDate)
        currentDate = display.getCell('A' + ((index * 17) + 1)).value
      }
      // The sheet has the same seventeen rows for every column, so a client type's
      // column still shows the dirty, repair and reject cells it can never hold.
      // Editing one would write a simulation that drives a cell permanently negative,
      // so those are dropped rather than imported. Filtered once here instead of at
      // each of the eleven reads above.
      const rejected = (out || []).filter(s => !this.simAllowedForType(s.operation, s.type))
      if (rejected.length > 0) {
        out = out.filter(s => this.simAllowedForType(s.operation, s.type))
        const types = [...new Set(rejected.map(s => s.type))].join(', ')
        this.$buefy.toast.open({
          message: `${rejected.length} edit(s) were ignored: ${types} are already prepared crates, so they only accept Prepared, Declassified, In Clean and Out Clean.`,
          duration: 8000,
          type: 'is-warning'
        })
      }
      if (out?.length <= 0 && simFalseShipments.length <= 0) {
        this.$buefy.toast.open({ message: 'No Simulations Found', duration: 10000, type: 'is-warning' })
      }
      this.days = out
      this.newFalseShipments = simFalseShipments
    },
    // Two requests, each one transaction. Both replace what they target, so after a
    // failure the import modal stays open and saving again is safe.
    async saveSimulations () {
      const saved = await this.runSave(async () => {
        if (this.newFalseShipments && this.newFalseShipments.length > 0) {
          await Forecast.createFakeShipments({ shipments: this.newFalseShipments, start: this.falseStart, end: this.falseEnd })
        }
        if (this.days && this.days.length > 0) {
          await Forecast.createSimulation(this.days)
        }
      })
      if (!saved) {
        return
      }
      this.resetData()
      this.getData()
      this.isImportingExcel = false
    },
    async exportToExcel () {
      const newWorkbook = new exceljs.Workbook()
      newWorkbook.creator = 'GPS Hub'
      newWorkbook.calcProperties.fullCalcOnLoad = true
      const sheet = newWorkbook.addWorksheet('Crate Forecasts')
      const backup = newWorkbook.addWorksheet('DONT TOUCH')

      const excelCollumns = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
      // Row labels are shared by every column, so they name both meanings when the
      // site has client-specific types: rows 6 and 7 carry washing/repair for an
      // ordinary column and preparation/declassification for a client one.
      const hasClientTypes = this.clientCrateTypes.length > 0
      // Production sims on or before the last production are ignored by the stock, as
      // on the day cards; the shipping sims resolve on their own rules and keep them all.
      const productionSims = this.rawSimData.filter(x => new Date(x.date).toLocaleDateString('en-CA') > this.lastDate)
      this.forecasts.forEach((forecast, numForecast) => {
        sheet.getCell('A' + (1 + (numForecast * 17) + 0)).value = new Date(forecast.date).toLocaleDateString('en-CA')
        sheet.getCell('B' + (1 + (numForecast * 17) + 0)).value = new Date(forecast.date).toLocaleString('en', { weekday: 'long' })
        if (new Date(forecast.date).toLocaleDateString('en-CA') === this.lastDate) {
          sheet.getCell('C' + (1 + (numForecast * 17) + 0)).value = 'Last Production'
          sheet.getCell('C' + (1 + (numForecast * 17) + 0)).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: '11b800' }
          }
        }
        sheet.getCell('B' + (1 + (numForecast * 17) + 0)).value = new Date(forecast.date).toLocaleString('en', { weekday: 'long' })
        sheet.getCell('A' + (1 + (numForecast * 17) + 1)).value = 'Crate Types'
        sheet.getCell('A' + (1 + (numForecast * 17) + 1)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell('A' + (1 + (numForecast * 17) + 2)).value = 'Dirty'
        sheet.getCell('A' + (1 + (numForecast * 17) + 2)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell('A' + (1 + (numForecast * 17) + 3)).value = 'Repair'
        sheet.getCell('A' + (1 + (numForecast * 17) + 3)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell('A' + (1 + (numForecast * 17) + 4)).value = 'Clean'
        sheet.getCell('A' + (1 + (numForecast * 17) + 4)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell('A' + (1 + (numForecast * 17) + 5)).value = hasClientTypes ? 'Washed / Prepared' : 'Washed'
        sheet.getCell('A' + (1 + (numForecast * 17) + 5)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell('A' + (1 + (numForecast * 17) + 6)).value = hasClientTypes ? 'Repaired / Declassified' : 'Repaired'
        sheet.getCell('A' + (1 + (numForecast * 17) + 6)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell('A' + (1 + (numForecast * 17) + 7)).value = 'In Dirty'
        sheet.getCell('A' + (1 + (numForecast * 17) + 7)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell('A' + (1 + (numForecast * 17) + 8)).value = 'In Repair'
        sheet.getCell('A' + (1 + (numForecast * 17) + 8)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell('A' + (1 + (numForecast * 17) + 9)).value = 'In Clean'
        sheet.getCell('A' + (1 + (numForecast * 17) + 9)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell('A' + (1 + (numForecast * 17) + 10)).value = 'Out Dirty'
        sheet.getCell('A' + (1 + (numForecast * 17) + 10)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell('A' + (1 + (numForecast * 17) + 11)).value = 'Out Repair'
        sheet.getCell('A' + (1 + (numForecast * 17) + 11)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell('A' + (1 + (numForecast * 17) + 12)).value = 'Out Clean'
        sheet.getCell('A' + (1 + (numForecast * 17) + 12)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell('A' + (1 + (numForecast * 17) + 13)).value = 'Rej Clean>Dirty'
        sheet.getCell('A' + (1 + (numForecast * 17) + 13)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell('A' + (1 + (numForecast * 17) + 14)).value = 'Rej Clean>Repair'
        sheet.getCell('A' + (1 + (numForecast * 17) + 14)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell('A' + (1 + (numForecast * 17) + 15)).value = 'Rej Dirty>Repair'
        sheet.getCell('A' + (1 + (numForecast * 17) + 15)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        if (!this.washingDays.includes(new Date(forecast.date).getDay())) {
          sheet.getCell('D' + (1 + (numForecast * 17) + 0)).value = 'No Washing Today'
        }

        forecast.types.forEach((type, index) => {
          // A client-specific type's column behaves differently: it has no dirty or
          // repair stock, its production row is a preparation that comes out of ANOTHER
          // column (one of its root type's stocks), and row 7 carries declassification
          // instead of repair. `clientColumnsOfRoot` is how a root column finds the
          // client columns drawing on it, split by which of its stocks they draw on: a
          // type whose crates are washed twice is prepared out of the root's CLEAN
          // stock, everything else out of its DIRTY stock.
          const isClientColumn = this.isClientType(type)
          const clientColumnsOfRoot = { DIRTY: [], CLEAN: [] }
          forecast.types.forEach((other, otherIndex) => {
            const clientType = this.clientCrateTypes.find(c => c.name === other)
            if (!clientType || clientType.rootType !== type) return
            clientColumnsOfRoot[clientType.doubleWash ? 'CLEAN' : 'DIRTY'].push(otherIndex)
          })
          // Type Name
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 1)).value = type
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 1)).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'd3d3d3' }
          }
          // First table
          if (numForecast === 0) {
            // Set column widths (only done once)
            let colBuffer = sheet.getColumn('A')
            colBuffer.width = 11
            colBuffer = sheet.getColumn('L')
            colBuffer.width = 9
            colBuffer = sheet.getColumn('M')
            colBuffer.width = 20
            colBuffer = sheet.getColumn('N')
            colBuffer.width = 9
            colBuffer = sheet.getColumn('R')
            colBuffer.width = 9
            colBuffer = sheet.getColumn('T')
            colBuffer.width = 10
            colBuffer = sheet.getColumn(excelCollumns[forecast.types.length + 4])
            colBuffer.width = 10
            colBuffer = sheet.getColumn(excelCollumns[forecast.types.length + 5])
            colBuffer.width = 12.5
            // Dirty Stock
            sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 2)).value = forecast.values[type]['DIRTY'].num_crates
            // Repair Stock
            sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 3)).value = forecast.values[type]['REPAIR'].num_crates
            // Clean Stock
            sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 4)).value = forecast.values[type]['CLEAN'].num_crates
          } else {
            // Each stock carries over from yesterday, gaining the movements that end
            // in it and losing the ones that leave it. Rows in yesterday's block:
            // 3 dirty, 4 repair, 5 clean, 6 washed, 7 repaired, 8-10 in dirty/repair/clean,
            // 11-13 out dirty/repair/clean, 14-16 rej clean>dirty/clean>repair/dirty>repair.
            const prevCell = (row) => excelCollumns[index + 1] + (((numForecast - 1) * 17) + row)
            // Yesterday's row `row` in another type's column — how the root type
            // reaches the client columns prepared out of its stock.
            const prevCellOf = (col, row) => excelCollumns[col + 1] + (((numForecast - 1) * 17) + row)
            // What the client columns listed took out of this stock yesterday, and gave
            // back to it: row 6 is their preparation, row 7 their declassification.
            const preparedOutOf = (cols) => cols.map(c => '-' + prevCellOf(c, 6) + '+' + prevCellOf(c, 7)).join('')
            if (isClientColumn) {
              // A client type has no dirty or repair stock of its own: it is prepared
              // clean and leaves clean, and row 6 is its preparation while row 7 is its
              // declassification. Carried forward rather than written as 0 so the
              // column never silently drops whatever the first day started from.
              sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 2)).value = { formula: prevCell(3) }
              sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 3)).value = { formula: prevCell(4) }
            } else {
              // Crates prepared for a single-washed client type leave this type's dirty
              // stock, and declassified ones come back to it. A double-washed one draws
              // on the clean stock instead, so it is added to that formula below.
              const preparedOut = preparedOutOf(clientColumnsOfRoot.DIRTY)
              // Dirty Stock
              sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 2)).value = { formula: prevCell(3) + '-' + prevCell(6) + '+' + prevCell(7) + '+' + prevCell(8) + '-' + prevCell(11) + '+' + prevCell(14) + '-' + prevCell(16) + preparedOut }
              // Repair Stock
              sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 3)).value = { formula: prevCell(4) + '-' + prevCell(7) + '+' + prevCell(9) + '-' + prevCell(12) + '+' + prevCell(15) + '+' + prevCell(16) }
            }
            // Clean Stock
            sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 4)).value = isClientColumn
              // prepared in, declassified out, shipped in and out clean
              ? { formula: prevCell(5) + '+' + prevCell(6) + '-' + prevCell(7) + '+' + prevCell(10) + '-' + prevCell(13) }
              // The double-washed client columns rooted here are prepared out of this
              // stock, since their crates went through the ordinary wash first.
              : { formula: prevCell(5) + '+' + prevCell(6) + '+' + prevCell(10) + '-' + prevCell(13) + '-' + prevCell(14) + '-' + prevCell(15) + preparedOutOf(clientColumnsOfRoot.CLEAN) }
          }
          // Washed, or Prepared for a client type
          const prodOp = this.productionSimOperation(type)
          let simWashed = productionSims.filter(x => { return (x.type === type || (type === '5.X' && (x.type === '5.2' || x.type === '5.5'))) && x.operation === prodOp && new Date(x.date).toLocaleDateString('en-ca') === new Date(forecast.date).toLocaleDateString('en-CA') })
          const washedValue = forecast.washed[type] ? forecast.washed[type] : simWashed.length > 0 ? simWashed[0].num_crates : isClientColumn || this.meanIsGated(forecast) ? 0 : this.crateMeans[type + '__' + 'washing']
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 5)).value = washedValue
          if (simWashed.length > 0) {
            sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 5)).font = {
              color: { argb: '4ca04c' }
            }
          }
          backup.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 5)).value = washedValue
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 5)).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'd8e4bc' }
          }
          // Repaired, or Declassified for a client type
          const reverseOp = isClientColumn ? SPECIAL_CLIENT_DECLASSIFY : 'repair'
          let simRepaired = productionSims.filter(x => { return (x.type === type || (type === '5.X' && (x.type === '5.2' || x.type === '5.5'))) && x.operation === reverseOp && new Date(x.date).toLocaleDateString('en-ca') === new Date(forecast.date).toLocaleDateString('en-CA') })
          // Declared first, then simulated, then the mean — but a client type has no
          // mean for declassification, so it stops at 0.
          const repairedValue = forecast.repaired[type]
            ? forecast.repaired[type]
            : simRepaired.length > 0 ? simRepaired[0].num_crates : isClientColumn || this.meanIsGated(forecast) ? 0 : this.crateMeans[type + '__' + 'repair']
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 6)).value = repairedValue
          if (simRepaired.length > 0) {
            sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 6)).font = {
              color: { argb: '4ca04c' }
            }
          }
          backup.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 6)).value = repairedValue
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 6)).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'd8e4bc' }
          }
          // In Dirty
          let simInDirty = this.rawSimData.filter(x => { return (x.type === type || (type === '5.X' && (x.type === '5.2' || x.type === '5.5'))) && x.operation === 'in__dirty' && new Date(x.date).toLocaleDateString('en-ca') === new Date(forecast.date).toLocaleDateString('en-CA') })
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 7)).value = isClientColumn ? 0 : this.crateIOs[type][numForecast].dirtyIn
          if (simInDirty.length > 0) {
            sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 7)).font = {
              color: { argb: '4ca04c' }
            }
          }
          backup.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 7)).value = isClientColumn ? 0 : this.crateIOs[type][numForecast].dirtyIn
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 7)).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'fcd5b4' }
          }
          // In Repair
          let simInRepair = this.rawSimData.filter(x => { return (x.type === type || (type === '5.X' && (x.type === '5.2' || x.type === '5.5'))) && x.operation === 'in__repair' && new Date(x.date).toLocaleDateString('en-ca') === new Date(forecast.date).toLocaleDateString('en-CA') })
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 8)).value = isClientColumn ? 0 : this.crateIOs[type][numForecast].repairIn
          if (simInRepair.length > 0) {
            sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 8)).font = {
              color: { argb: '4ca04c' }
            }
          }
          backup.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 8)).value = isClientColumn ? 0 : this.crateIOs[type][numForecast].repairIn
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 8)).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'fcd5b4' }
          }
          // In Clean
          let simInClean = this.rawSimData.filter(x => { return (x.type === type || (type === '5.X' && (x.type === '5.2' || x.type === '5.5'))) && x.operation === 'in__clean' && new Date(x.date).toLocaleDateString('en-ca') === new Date(forecast.date).toLocaleDateString('en-CA') })
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 9)).value = this.crateIOs[type][numForecast].cleanIn
          if (simInClean.length > 0) {
            sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 9)).font = {
              color: { argb: '4ca04c' }
            }
          }
          backup.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 9)).value = this.crateIOs[type][numForecast].cleanIn
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 9)).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'fcd5b4' }
          }
          // Out Dirty
          let simOutDirty = this.rawSimData.filter(x => { return (x.type === type || (type === '5.X' && (x.type === '5.2' || x.type === '5.5'))) && x.operation === 'out__dirty' && new Date(x.date).toLocaleDateString('en-ca') === new Date(forecast.date).toLocaleDateString('en-CA') })
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 10)).value = isClientColumn ? 0 : this.crateIOs[type][numForecast].outDirty
          if (simOutDirty.length > 0) {
            sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 10)).font = {
              color: { argb: '4ca04c' }
            }
          }
          backup.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 10)).value = isClientColumn ? 0 : this.crateIOs[type][numForecast].outDirty
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 10)).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'b7dee8' }
          }
          // Out Repair
          let simOutRepair = this.rawSimData.filter(x => { return (x.type === type || (type === '5.X' && (x.type === '5.2' || x.type === '5.5'))) && x.operation === 'out__repair' && new Date(x.date).toLocaleDateString('en-ca') === new Date(forecast.date).toLocaleDateString('en-CA') })
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 11)).value = isClientColumn ? 0 : this.crateIOs[type][numForecast].outRepair
          if (simOutRepair.length > 0) {
            sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 11)).font = {
              color: { argb: '4ca04c' }
            }
          }
          backup.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 11)).value = isClientColumn ? 0 : this.crateIOs[type][numForecast].outRepair
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 11)).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'b7dee8' }
          }
          // Out Clean
          let simOutClean = this.rawSimData.filter(x => { return (x.type === type || (type === '5.X' && (x.type === '5.2' || x.type === '5.5'))) && x.operation === 'out__clean' && new Date(x.date).toLocaleDateString('en-ca') === new Date(forecast.date).toLocaleDateString('en-CA') })
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 12)).value = this.crateIOs[type][numForecast].outClean
          if (simOutClean.length > 0) {
            sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 12)).font = {
              color: { argb: '4ca04c' }
            }
          }
          backup.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 12)).value = this.crateIOs[type][numForecast].outClean
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 12)).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'b7dee8' }
          }
          // Rejections. Historical days use the actual collated movement sums
          // (incl. 0). Future days fall back to an existing reject simulation, then
          // to the reject-mean prediction — mirroring how Washed/Repaired behave so
          // Excel's recreated stock tracks the API forecast instead of drifting.
          // Future-day reject prediction: reject means are keyed by transition
          // (type__reject__FROM__TO — see getData). Absent transitions yield 0.
          const rejectMean = (from, to) => this.crateMeans[type + '__reject__' + from + '__' + to] || 0
          // A client type's crates are already washed and repaired, so nothing is ever
          // rejected out of that stock: gate its three reject rows to 0 outright.
          const rejectGated = isClientColumn || this.meanIsGated(forecast)
          // Rej Clean>Dirty
          let simRejCleanDirty = productionSims.filter(x => { return (x.type === type || (type === '5.X' && (x.type === '5.2' || x.type === '5.5'))) && x.operation === 'forklift__cleanRejectedDirty' && new Date(x.date).toLocaleDateString('en-ca') === new Date(forecast.date).toLocaleDateString('en-CA') })
          let rejCleanDirtyVal = forecast.rejCleanDirty[type] !== null ? forecast.rejCleanDirty[type] : simRejCleanDirty.length > 0 ? simRejCleanDirty[0].num_crates : rejectGated ? 0 : rejectMean('CLEAN', 'DIRTY')
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 13)).value = rejCleanDirtyVal
          if (simRejCleanDirty.length > 0) {
            sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 13)).font = {
              color: { argb: '4ca04c' }
            }
          }
          backup.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 13)).value = rejCleanDirtyVal
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 13)).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'e6b8af' }
          }
          // Rej Clean>Repair
          let simRejCleanRepair = productionSims.filter(x => { return (x.type === type || (type === '5.X' && (x.type === '5.2' || x.type === '5.5'))) && x.operation === 'forklift__cleanRejectedRepair' && new Date(x.date).toLocaleDateString('en-ca') === new Date(forecast.date).toLocaleDateString('en-CA') })
          let rejCleanRepairVal = forecast.rejCleanRepair[type] !== null ? forecast.rejCleanRepair[type] : simRejCleanRepair.length > 0 ? simRejCleanRepair[0].num_crates : rejectGated ? 0 : rejectMean('CLEAN', 'REPAIR')
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 14)).value = rejCleanRepairVal
          if (simRejCleanRepair.length > 0) {
            sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 14)).font = {
              color: { argb: '4ca04c' }
            }
          }
          backup.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 14)).value = rejCleanRepairVal
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 14)).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'e6b8af' }
          }
          // Rej Dirty>Repair
          let simRejDirtyRepair = productionSims.filter(x => { return (x.type === type || (type === '5.X' && (x.type === '5.2' || x.type === '5.5'))) && x.operation === 'forklift__dirtyRejectedRepair' && new Date(x.date).toLocaleDateString('en-ca') === new Date(forecast.date).toLocaleDateString('en-CA') })
          let rejDirtyRepairVal = forecast.rejDirtyRepair[type] !== null ? forecast.rejDirtyRepair[type] : simRejDirtyRepair.length > 0 ? simRejDirtyRepair[0].num_crates : rejectGated ? 0 : rejectMean('DIRTY', 'REPAIR')
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 15)).value = rejDirtyRepairVal
          if (simRejDirtyRepair.length > 0) {
            sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 15)).font = {
              color: { argb: '4ca04c' }
            }
          }
          backup.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 15)).value = rejDirtyRepairVal
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 17) + 15)).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'e6b8af' }
          }
        })
        sheet.getCell(excelCollumns[forecast.types.length + 1] + (1 + (numForecast * 17) + 1)).value = 'TOTAL'
        sheet.getCell(excelCollumns[forecast.types.length + 1] + (1 + (numForecast * 17) + 1)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell(excelCollumns[forecast.types.length + 2] + (1 + (numForecast * 17) + 1)).value = '% MAX'
        sheet.getCell(excelCollumns[forecast.types.length + 2] + (1 + (numForecast * 17) + 1)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell(excelCollumns[forecast.types.length + 3] + (1 + (numForecast * 17) + 1)).value = '% MIN'
        sheet.getCell(excelCollumns[forecast.types.length + 3] + (1 + (numForecast * 17) + 1)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell(excelCollumns[forecast.types.length + 4] + (1 + (numForecast * 17) + 1)).value = 'TOTAL ALL'
        sheet.getCell(excelCollumns[forecast.types.length + 4] + (1 + (numForecast * 17) + 1)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell(excelCollumns[forecast.types.length + 5] + (1 + (numForecast * 17) + 1)).value = '% MAX TOTAL'
        sheet.getCell(excelCollumns[forecast.types.length + 5] + (1 + (numForecast * 17) + 1)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        // The TOTAL / % MAX / % MIN columns sit on the stock rows, so each one follows
        // its status: row 2 dirty (min S1), row 3 repair (min U1), row 4 clean (max O1,
        // min Q1). TOTAL ALL and % MAX TOTAL cover the three stocks together, so they
        // stay on the first of the rows and their SUMIF spans all three.
        sheet.getCell(excelCollumns[forecast.types.length + 1] + (1 + (numForecast * 17) + 2)).value = { formula: 'SUMIF(' + 'B' + (1 + (numForecast * 17) + 2) + ':' + excelCollumns[forecast.types.length] + (1 + (numForecast * 17) + 2) + ',">0"' + ')' }
        sheet.getCell(excelCollumns[forecast.types.length + 3] + (1 + (numForecast * 17) + 2)).value = { formula: excelCollumns[forecast.types.length + 1] + (1 + (numForecast * 17) + 2) + '/S1*100' }
        sheet.getCell(excelCollumns[forecast.types.length + 4] + (1 + (numForecast * 17) + 2)).value = { formula: 'SUMIF(' + 'B' + (1 + (numForecast * 17) + 2) + ':' + excelCollumns[forecast.types.length] + (1 + (numForecast * 17) + 4) + ',">0"' + ')' }
        sheet.getCell(excelCollumns[forecast.types.length + 5] + (1 + (numForecast * 17) + 2)).value = { formula: excelCollumns[forecast.types.length + 4] + (1 + (numForecast * 17) + 3) + '/M1*100' }
        sheet.getCell(excelCollumns[forecast.types.length + 1] + (1 + (numForecast * 17) + 3)).value = { formula: 'SUMIF(' + 'B' + (1 + (numForecast * 17) + 3) + ':' + excelCollumns[forecast.types.length] + (1 + (numForecast * 17) + 3) + ',">0"' + ')' }
        sheet.getCell(excelCollumns[forecast.types.length + 3] + (1 + (numForecast * 17) + 3)).value = { formula: excelCollumns[forecast.types.length + 1] + (1 + (numForecast * 17) + 3) + '/U1*100' }
        sheet.getCell(excelCollumns[forecast.types.length + 1] + (1 + (numForecast * 17) + 4)).value = { formula: 'SUMIF(' + 'B' + (1 + (numForecast * 17) + 4) + ':' + excelCollumns[forecast.types.length] + (1 + (numForecast * 17) + 4) + ',">0"' + ')' }
        sheet.getCell(excelCollumns[forecast.types.length + 2] + (1 + (numForecast * 17) + 4)).value = { formula: excelCollumns[forecast.types.length + 1] + (1 + (numForecast * 17) + 4) + '/O1*100' }
        sheet.getCell(excelCollumns[forecast.types.length + 3] + (1 + (numForecast * 17) + 4)).value = { formula: excelCollumns[forecast.types.length + 1] + (1 + (numForecast * 17) + 4) + '/Q1*100' }
        if (forecast.inventory) {
          sheet.getCell(excelCollumns[forecast.types.length + 6] + (1 + (numForecast * 17) + 1)).value = 'INVENTORY TODAY'
          sheet.getCell('A' + (1 + (numForecast * 17) + 0)).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'ffff00' }
          }
        }
        sheet.getCell(excelCollumns[forecast.types.length + 9] + (1 + (numForecast * 17) + 1)).value = 'Name'
        sheet.getCell(excelCollumns[forecast.types.length + 9] + (1 + (numForecast * 17) + 1)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell(excelCollumns[forecast.types.length + 10] + (1 + (numForecast * 17) + 1)).value = 'Quantity'
        sheet.getCell(excelCollumns[forecast.types.length + 10] + (1 + (numForecast * 17) + 1)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        let dayFalseShipments = this.falseShipments.filter((x) => { return new Date(x.date).toLocaleDateString('en-CA') === new Date(forecast.date).toLocaleDateString('en-CA') })
        for (var loop = 0; loop < dayFalseShipments.length; loop += 1) {
          sheet.getCell(excelCollumns[forecast.types.length + 9] + (1 + (numForecast * 17) + 2 + loop)).value = dayFalseShipments[loop].name // name
          sheet.getCell(excelCollumns[forecast.types.length + 9] + (1 + (numForecast * 17) + 2 + loop)).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'f2f2f2' }
          }
          sheet.getCell(excelCollumns[forecast.types.length + 10] + (1 + (numForecast * 17) + 2 + loop)).value = dayFalseShipments[loop].qty // quantity
          sheet.getCell(excelCollumns[forecast.types.length + 10] + (1 + (numForecast * 17) + 2 + loop)).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'f2f2f2' }
          }
        }
        if (forecast.comment) {
          sheet.getCell(excelCollumns[forecast.types.length + 7] + (1 + (numForecast * 17) + 1)).value = forecast.comment?.text
          backup.getCell(excelCollumns[forecast.types.length + 7] + (1 + (numForecast * 17) + 1)).value = forecast.comment?.text
        }
        if (numForecast === 0) {
          sheet.getCell('L1').value = 'Max Total'
          sheet.getCell('M1').value = this.target.max_total
          backup.getCell('M1').value = this.target.max_total
          sheet.getCell('N1').value = 'Max Clean'
          sheet.getCell('O1').value = this.target.max_clean
          backup.getCell('O1').value = this.target.max_clean
          sheet.getCell('P1').value = 'Min Clean'
          sheet.getCell('Q1').value = this.target.min_clean
          backup.getCell('Q1').value = this.target.min_clean
          sheet.getCell('R1').value = 'Min Dirty'
          sheet.getCell('S1').value = this.target.min_dirty
          backup.getCell('S1').value = this.target.min_dirty
          sheet.getCell('T1').value = 'Min Repair'
          sheet.getCell('U1').value = this.target.min_repair
          backup.getCell('U1').value = this.target.min_repair
        }
      })
      var buffer = await newWorkbook.xlsx.writeBuffer('exportedData.xlsx')
      saveAs(new Blob([buffer]), this.selectedPlant.name + '-' + this.selectedPlant.id + new Date().toLocaleDateString('en-CA') + '.xlsx')
    },
    async submitComment (archive) {
      // `editableComment` is a direct reference to the forecast object in
      // `this.forecasts`, and its `comment` property always pre-exists (either a
      // comment object or null, see getData). So once the API call succeeds we can
      // mutate that comment in place for an instant visual update, instead of
      // triggering a full resetData()/getData() reload.
      if (this.editableComment.comment && (this.editableComment.comment.text !== this.bufferText)) {
        Forecast.updateComment({
          id_site: this.editableComment.comment.id_site,
          date: this.editableComment.comment.date,
          text: this.bufferText,
          id: this.editableComment.comment.id
        }).then(() => {
          this.editableComment.comment.text = this.bufferText
          this.isEditingComment = false
        })
      }
      if (!this.editableComment.comment && (this.bufferText && this.bufferText.length > 0)) {
        const date = this.editableComment.date
        Forecast.createComment({
          id_site: this.storedPlantId,
          date,
          text: this.bufferText
        }).then(() => {
          // createComment returns only a 200 (no body), so fetch the comments for
          // the current range to recover the new comment's id — much cheaper than
          // reloading the whole forecast dataset.
          return Forecast.getComments({
            start: this.dateStart.toLocaleDateString('en-CA'),
            end: this.dateEnd.toLocaleDateString('en-CA'),
            siteId: this.selectedPlant.id
          })
        }).then((comments) => {
          const created = comments.find(x => new Date(x.date).toLocaleDateString('en-CA') === date)
          this.editableComment.comment = created || {
            id_site: this.storedPlantId,
            date,
            text: this.bufferText
          }
          this.isEditingComment = false
        })
      }
      if (this.editableComment.comment && archive === true) {
        Forecast.deleteComment({
          id: this.editableComment.comment.id
        }).then(() => {
          this.editableComment.comment = null
          this.isEditingComment = false
        })
      }
    },
    // Normalises an editable field to a Number (or null when cleared) so that
    // comparisons against the original value don't drop decimals. Using parseInt
    // here previously truncated float means (e.g. 12.53 -> 12), which registered
    // a phantom "change" on every type and created unwanted simulations.
    simNum (val) {
      return val === null || val === undefined || val === '' ? null : Number(val)
    },
    async submitEdit () {
      var outSimulations = []
      if (!this.editBatch) {
        this.editDateEnd = this.editDateStart
      }
      try {
        const editFields = [
          { editable: this.editableWashed, original: this.originalWashed, operation: (type) => this.productionSimOperation(type) },
          { editable: this.editableRepaired, original: this.originalRepaired, operation: 'repair' },
          { editable: this.editableDeclassified, original: this.originalDeclassified, operation: SPECIAL_CLIENT_DECLASSIFY },
          { editable: this.editableInDirty, original: this.originalInDirty, operation: 'in__dirty' },
          { editable: this.editableInRepair, original: this.originalInRepair, operation: 'in__repair' },
          { editable: this.editableInClean, original: this.originalInClean, operation: 'in__clean' },
          { editable: this.editableOutDirty, original: this.originalOutDirty, operation: 'out__dirty' },
          { editable: this.editableOutRepair, original: this.originalOutRepair, operation: 'out__repair' },
          { editable: this.editableOutClean, original: this.originalOutClean, operation: 'out__clean' },
          { editable: this.editableRejCleanDirty, original: this.originalRejCleanDirty, operation: 'forklift__cleanRejectedDirty' },
          { editable: this.editableRejCleanRepair, original: this.originalRejCleanRepair, operation: 'forklift__cleanRejectedRepair' },
          { editable: this.editableRejDirtyRepair, original: this.originalRejDirtyRepair, operation: 'forklift__dirtyRejectedRepair' }
        ]
        for (var d = new Date(this.editDateStart); d <= this.editDateEnd; d.setDate(d.getDate() + 1)) {
          if (this.editBatch && (!this.washingDays.includes(d.getDay()) || this.holidays.includes(d.toLocaleDateString('en-CA')))) {

          } else {
            this.editableForecast.types.forEach((type) => {
              editFields.forEach(({ editable, original, operation }) => {
                // The produced figure is a different operation per type, so it is
                // resolved here rather than fixed in the list above.
                operation = typeof operation === 'function' ? operation(type) : operation
                // Anything a type cannot carry is skipped — see simAllowedForType.
                // Writing it would create a simulation the API discards anyway.
                if (!this.simAllowedForType(operation, type)) return
                const value = this.simNum(editable[type])
                if (value !== this.simNum(original[type])) {
                  outSimulations.push({
                    num_crates: Math.round(value),
                    operation: operation,
                    id_site: this.storedPlantId,
                    date: d.toLocaleDateString('en-CA'),
                    type: type
                  })
                }
              })
            })
          }
        }
        if (!await this.runSave(() => Forecast.createSimulation(outSimulations))) {
          return
        }
        this.editableForecast = null
        this.originalForecast = null
        this.resetData()
        this.getData()
        this.isEditingForecast = false
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
      }
    },
    pinData () {
      this.pinnedData.push({
        crateForecast: this.crateForecasts.filter(x => x.type === this.activeTab),
        storedDate: this.storedDate,
        key: this.pinnedData.length
      })
    },
    syncToolbarMetrics () {
      const el = this.$refs.toolbar
      if (!el) return
      const rect = el.getBoundingClientRect()
      this.toolbarHeight = rect.height
      // Only the unfolded height is worth remembering: it is the space the folded bar
      // has to reserve further down the page.
      if (!this.isToolbarCondensed) this.toolbarFullHeight = rect.height
    },
    // 'top', where the whole block fits on screen anyway, or 'scrolled', where the
    // forecast wants the room.
    toolbarRegion () {
      const scrolled = window.scrollY || document.documentElement.scrollTop || 0
      return scrolled <= TOOLBAR_CONDENSE_SCROLL ? 'top' : 'scrolled'
    },
    onPageScroll () {
      // Scroll fires far faster than the bar needs to change; one check per frame.
      if (this.toolbarScrollFrame) return
      this.toolbarScrollFrame = window.requestAnimationFrame(() => {
        this.toolbarScrollFrame = null
        this.syncToolbarMetrics()
        const region = this.toolbarRegion()
        // A choice made here stands until the page moves out of the part it was made
        // in; after that the bar goes back to folding on its own.
        if (region === this.toolbarOverrideRegion) return
        this.toolbarOverrideRegion = null
        this.setToolbarCondensed(region === 'scrolled')
      })
    },
    toggleToolbar () {
      this.toolbarOverrideRegion = this.toolbarRegion()
      this.setToolbarCondensed(!this.isToolbarCondensed)
    },
    setToolbarCondensed (condensed) {
      if (condensed === this.isToolbarCondensed) return
      this.isToolbarCondensed = condensed
      // Remeasure once the rows have actually gone, in case nothing else triggers it.
      this.$nextTick(this.syncToolbarMetrics)
    },
    async resetSimulations (flags = {}) {
      await Forecast.resetSimulations({
        id_site: this.selectedPlant.id,
        ...flags
      })
      this.isResetting = false
      this.resetData()
      this.getData()
    },
    // A row's id is SAP's COLLECT_ID, which is only sometimes the OB; displayed_ob
    // (DELIV_ID) always is. Rows with no SAP counterpart — predictions, Simorep
    // previsions — have no displayed_ob and show their id instead.
    displayedOb (row) {
      return row.displayed_ob || row.id
    },
    // Same for a moved_shipment record, whose `ob` is the crate id. Falls back to
    // the id when its SAP row is no longer loaded.
    movedOb (move) {
      return this.displayedObById[move.ob + '__' + move.shipment_type] || this.displayedObById[move.ob] || move.ob
    },
    // The site is always one end of a moved shipment, so the half worth showing is
    // the other one: the origin of an inbound shipment, the destination of an
    // outbound. Same convention as the Origin/Destination columns of the day cards,
    // name included — a hand-written prediction has no SAP row to name it, so it
    // falls back to the code the user typed.
    movedCounterparty (row) {
      const isIn = ['COL', 'TRFI', 'NCR'].includes(row.shipment_type)
      const named = row.ob == null
        ? null
        : (this.collectNamesByOb[row.ob + '__' + row.shipment_type] || this.collectNamesByOb[row.ob])
      const label = named || (isIn ? row.ship_from : row.ship_to)
      if (!label) return '—'
      return (isIn ? 'From ' : 'To ') + label
    },
    // Same formatting as the cells, so a date/crate filter matches what is displayed.
    resetRowDate (row) {
      if (this.resetTableType === 'movedShipments') return String(row.moved_date).slice(0, 10)
      return new Date(row.date).toLocaleDateString('en-CA')
    },
    resetRowCrates (row) {
      const value = this.resetTableType === 'falseShipments' ? row.qty : row.num_crates
      return value === null || value === undefined ? null : parseInt(value)
    },
    resetRowType (row) {
      if (this.resetTableType === 'falseShipments') return null
      return this.resetTableType === 'movedShipments' ? row.crate_type : row.type
    },
    // Moved shipments have no simulation operation; their shipment type (COL, DEL...)
    // is the equivalent.
    resetRowOperation (row) {
      if (this.resetTableType === 'falseShipments') return null
      return this.resetTableType === 'movedShipments' ? row.shipment_type : row.operation
    },
    clearResetFilters () {
      this.resetFilters = { date: null, crates: '', type: '', operation: '' }
      this.resetFilterOpen = { date: false, type: false, operation: false, crates: false }
    },
    closeResetFilter (key) {
      this.resetFilters[key] = key === 'date' ? null : ''
      this.resetFilterOpen[key] = false
    },
    async archiveSelected () {
      if (this.selectedResetRows.length === 0) {
        return
      }
      if (this.resetTableType === 'falseShipments') {
        await Forecast.archiveFakeShipments(this.selectedResetRows)
      } else if (this.resetTableType === 'movedShipments') {
        await Forecast.archiveMovedShipments(this.selectedResetRows)
      } else {
        await Forecast.archiveSimulations(this.selectedResetRows)
      }
      this.selectedResetRows = []
      this.resetData()
      this.getData()
    },
    // --- Moved shipments: shipment-view drag & drop, new predictions, submit ---
    normDate (d) {
      return d ? new Date(d).toLocaleDateString('en-CA') : null
    },
    // Rebuilds this.crateIOs from this.collects (+ the stored sim data) so the
    // shipment view reflects optimistic moves without a server round-trip. Assigns a
    // fresh object for reactivity.
    //
    // Every cell is resolved exactly the way the projection resolves it (see
    // applyShipping in crateForecastRepository): a day that has passed counts only
    // what SAP scanned, today and the days ahead count what is still planned too, a
    // simulation stands in for the whole side without ever talking below what was
    // already scanned, and a future working day is topped up to the collect mean for
    // the collections SAP has not booked yet. The figures shown here having been
    // built the same way as the stock beside them is the point: a green number the
    // projection never used is worse than no number at all.
    buildCrateIOs () {
      const crateIOs = {}
      const collTypes = this.target.types ? this.target.types.map(x => x.name) : []
      const endCollLoop = new Date(this.dateEnd).setDate(this.dateEnd.getDate() + 1)
      const simData = this.simData || []
      const todayStr = new Date().toLocaleDateString('en-CA')
      /* eslint-disable no-unmodified-loop-condition */
      collTypes.forEach(colltype => {
        crateIOs[colltype] = []
        // A pooled '5.X' column holds 5.2 and 5.5 as one stock, shipments and
        // simulations alike.
        const isType = (x) => x.type === colltype || (colltype === '5.X' && (x.type === '5.2' || x.type === '5.5'))
        for (var startCollLoop = new Date(this.dateStart); startCollLoop <= endCollLoop; startCollLoop.setDate(startCollLoop.getDate() + 1)) {
          const dayStr = startCollLoop.toLocaleDateString('en-CA')
          const isPast = dayStr < todayStr
          const isFuture = dayStr > todayStr
          let predColls = this.collects.filter(x => {
            return !x.done_date && !(x.shipment === null && x.done !== 'T' && (x.shipType === 'COL' || x.shipType === 'NCR' || x.shipType === 'TRFI')) && new Date(x.date).toLocaleDateString('en-CA') === dayStr && isType(x)
          })
          let doneColls = this.collects.filter(x => {
            return x.done_date && !(x.shipment === null && x.done !== 'T' && (x.shipType === 'COL' || x.shipType === 'NCR' || x.shipType === 'TRFI')) && new Date(x.done_date).toLocaleDateString('en-CA') === dayStr && isType(x)
          })
          // The figure the user simulated for one direction and status on this day, or
          // null if they left it to SAP. A day that has passed is settled and its own
          // simulation has expired with it.
          const daySim = (direction, status) => {
            if (isPast) return null
            const rows = simData.filter(x => {
              return x.operation === direction + '__' + status.toLowerCase() && isType(x) && new Date(x.date).toLocaleDateString('en-CA') === dayStr
            })
            return rows.length > 0 ? rows.reduce((total, x) => total + parseInt(x.num_crates), 0) : null
          }
          const sumStatus = (rows, shipTypes, status) => rows.filter(x => {
            return shipTypes.includes(x.shipType) && x.status === status
          }).reduce((total, x) => total + parseInt(x.num_crates), 0)
          // One side of one day, per status, plus the totals the collect mean and the
          // edit modal are weighed against.
          const resolveSide = (direction, shipTypes) => {
            const side = { CLEAN: 0, DIRTY: 0, REPAIR: 0, planned: 0, simulated: false }
            this.statusOrder.forEach((status) => {
              const scanned = isFuture ? 0 : sumStatus(doneColls, shipTypes, status)
              const planned = isPast ? 0 : sumStatus(predColls, shipTypes, status)
              side.planned += planned
              const sim = daySim(direction, status)
              if (sim === null) {
                side[status] = scanned + planned
                return
              }
              side.simulated = true
              side[status] = Math.max(sim, scanned)
            })
            return side
          }
          const inSide = resolveSide('in', ['COL', 'TRFI', 'NCR'])
          const outSide = resolveSide('out', ['DEL', 'TRFO'])
          // Collections SAP has not booked yet, which come back dirty. A simulated
          // dirty in has already spoken for them.
          let predictedIn = 0
          if (isFuture && this.washingDays.includes(startCollLoop.getDay()) && daySim('in', 'DIRTY') === null) {
            predictedIn = Math.max(0, (this.collMeans[colltype] || 0) - inSide.planned)
          }
          inSide.DIRTY += predictedIn
          crateIOs[colltype].push({
            collDate: dayStr,
            totalIn: inSide.CLEAN + inSide.DIRTY + inSide.REPAIR,
            cleanIn: inSide.CLEAN,
            dirtyIn: inSide.DIRTY,
            repairIn: inSide.REPAIR,
            totalOut: outSide.CLEAN + outSide.DIRTY + outSide.REPAIR,
            outClean: outSide.CLEAN,
            outDirty: outSide.DIRTY,
            outRepair: outSide.REPAIR,
            // What of the inbound figure is the collect mean rather than a booked
            // shipment — the page marks that prediction out from a real one.
            predictedIn: predictedIn,
            predCollsIn: predColls.filter(x => {
              return x.shipType === 'COL' || x.shipType === 'TRFI' || x.shipType === 'NCR'
            }),
            predCollsOut: predColls.filter(x => {
              return x.shipType === 'DEL' || x.shipType === 'TRFO'
            }),
            doneCollsIn: doneColls.filter(x => {
              return x.shipType === 'COL' || x.shipType === 'TRFI' || x.shipType === 'NCR'
            }),
            doneCollsOut: doneColls.filter(x => {
              return x.shipType === 'DEL' || x.shipType === 'TRFO'
            }),
            simIn: inSide.simulated,
            simOut: outSide.simulated
          })
        }
      })
      /* eslint-enable no-unmodified-loop-condition */
      this.crateIOs = crateIOs
    },
    // Combines the per-type crateIOs for one day into the {done,pred}Colls{In,Out}
    // buckets the shipment-view tables render from.
    computeDayMoves (dateStr) {
      let allColls = []
      const combined = { doneCollsIn: [], doneCollsOut: [], predCollsIn: [], predCollsOut: [] }
      this.selectedTypes.forEach((x) => {
        if (this.crateIOs[x.name]) {
          allColls = allColls.concat(this.crateIOs[x.name].filter((c) => c.collDate === dateStr))
        }
      })
      allColls.forEach((coll) => {
        combined.doneCollsIn = combined.doneCollsIn.concat(coll.doneCollsIn)
        combined.doneCollsOut = combined.doneCollsOut.concat(coll.doneCollsOut)
        combined.predCollsIn = combined.predCollsIn.concat(coll.predCollsIn)
        combined.predCollsOut = combined.predCollsOut.concat(coll.predCollsOut)
      })
      return combined
    },
    rebuildShipmentView () {
      this.buildCrateIOs()
      this.forecasts.forEach((f) => { f.dayMoves = this.computeDayMoves(f.date) })
    },
    isMovable (move) {
      return !move.done_date
    },
    moveOutline (move) {
      if (move._pending) return '2px dashed rgb(255, 140, 0)'
      if (move._moved) return '2px solid rgb(70, 110, 220)'
      return ''
    },
    moveTitle (move) {
      if (move._new) return 'New predicted shipment — pending, not yet saved'
      if (move._pending) return 'Moved to this day — pending, not yet saved'
      if (move._moved) return move.originDate ? ('Previously moved (originally ' + move.originDate + ')') : 'Previously moved / predicted shipment'
      return 'Drag to another day to move this shipment'
    },
    onDragStart (event, move) {
      if (!this.isMovable(move)) return
      this.draggingOb = move.id
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move'
        event.dataTransfer.setData('text/plain', String(move.id))
      }
      // Native HTML5 drag locks the wheel, so we auto-scroll the window when the
      // pointer nears the top/bottom edge — days are stacked vertically and moving
      // a shipment a week out means dragging well past the visible cards.
      this.dragPointerY = event.clientY
      this.startDragScroll()
    },
    onDragEnd () {
      this.draggingOb = null
      this.dragOverDate = null
      this.stopDragScroll()
    },
    onDragOver (event, dateStr) {
      if (!this.draggingOb) return
      this.dragOverDate = dateStr
      this.dragPointerY = event.clientY
    },
    onDrop (dateStr) {
      if (this.draggingOb) this.moveShipment(this.draggingOb, dateStr)
      this.draggingOb = null
      this.dragOverDate = null
      this.stopDragScroll()
    },
    startDragScroll () {
      if (this.dragScrollRaf) return
      const edge = 110 // px from a viewport edge where auto-scroll kicks in
      const maxSpeed = 24 // px per frame at the very edge
      const step = () => {
        if (!this.draggingOb) { this.dragScrollRaf = null; return }
        const y = this.dragPointerY
        const h = window.innerHeight
        let dy = 0
        if (y < edge) dy = -maxSpeed * (1 - y / edge)
        else if (y > h - edge) dy = maxSpeed * (1 - (h - y) / edge)
        if (dy !== 0) window.scrollBy(0, dy)
        this.dragScrollRaf = requestAnimationFrame(step)
      }
      this.dragScrollRaf = requestAnimationFrame(step)
    },
    stopDragScroll () {
      if (this.dragScrollRaf) {
        cancelAnimationFrame(this.dragScrollRaf)
        this.dragScrollRaf = null
      }
    },
    // Moves every un-scanned row that shares this OB to the target day. An OB can
    // be several material rows, but a truck can only arrive once — so they snap
    // together. Scanned rows (done_date) are left alone: a real scan always wins.
    moveShipment (ob, targetDateStr) {
      const rows = this.collects.filter((c) => c.id === ob && !c.done_date)
      if (rows.length === 0) return
      if (rows.every((c) => this.normDate(c.date) === targetDateStr)) return
      rows.forEach((c) => {
        c.date = targetDateStr + 'T00:00:00'
        c._pending = this.normDate(c.date) !== this.normDate(c._baseDate)
      })
      this.rebuildShipmentView()
    },
    openNewPrediction (dateStr) {
      // '5.X' is a reporting pool, not a real type: the option list collapses it to
      // 5.2, so the default has to as well or an untouched dropdown submits '5.X'.
      const defaultType = this.selectedTypes.length > 0 ? this.selectedTypes[0].name : '5.2'
      const crateType = defaultType === '5.X' ? '5.2' : defaultType
      this.newPrediction = {
        shipment_type: 'COL',
        ship_from: '',
        ship_to: '',
        crate_type: crateType,
        // A client type only ever holds clean stock.
        crate_status: this.isClientType(crateType) ? 'CLEAN' : 'DIRTY',
        num_crates: null
      }
      this.predictionDate = new Date(dateStr + 'T00:00:00')
      this.isAddingPrediction = true
    },
    addPrediction () {
      if (!this.predictionDate || !this.newPrediction.num_crates) {
        this.$buefy.toast.open({ message: 'A date and quantity are required', duration: 4000, type: 'is-warning' })
        return
      }
      const dateStr = this.predictionDate.toLocaleDateString('en-CA')
      const key = 'new-' + (this.pendingNewCounter++)
      // The site is always one end of the shipment: it arrives here (in) or leaves from here (out).
      // Force the site-side code to the site id so the forecast engine counts it on reload.
      const shipFrom = this.predictionIsIn ? this.newPrediction.ship_from : this.storedPlantId
      const shipTo = this.predictionIsIn ? this.storedPlantId : this.newPrediction.ship_to
      this.collects.push({
        id: 'PENDING-' + key,
        _key: key,
        _new: true,
        _pending: true,
        _moved: true,
        _baseDate: null,
        done: 'F',
        shipment: 'PREVISION',
        ship_to: shipTo,
        ship_from: shipFrom,
        name: this.predictionIsIn ? shipFrom : shipTo,
        shipType: this.newPrediction.shipment_type,
        date: dateStr + 'T00:00:00',
        originDate: null,
        type: this.newPrediction.crate_type,
        status: this.newPrediction.crate_status,
        num_crates: parseInt(this.newPrediction.num_crates) || 0,
        done_date: null,
        relic: false,
        naval: false,
        route: null
      })
      this.rebuildShipmentView()
      this.isAddingPrediction = false
    },
    discardPending () {
      this.collects = this.collects.filter((c) => !c._new)
      this.collects.forEach((c) => {
        c.date = c._baseDate
        c._pending = false
      })
      this.rebuildShipmentView()
    },
    async submitMoves () {
      if (this.pendingCount === 0) return
      this.isSubmittingMoves = true
      const createPayloads = []
      const editPayloads = []
      const movedByOb = {}
      this.collects.forEach((c) => {
        if (c._new) {
          createPayloads.push({
            id_site: this.storedPlantId,
            is_new: true,
            shipment_type: c.shipType,
            ship_from: c.ship_from,
            ship_to: c.ship_to,
            crate_type: c.type,
            crate_status: c.status,
            moved_date: this.normDate(c.date),
            num_crates: parseInt(c.num_crates) || 0
          })
        } else if (this.normDate(c.date) !== this.normDate(c._baseDate)) {
          movedByOb[c.id] = c
        }
      })
      Object.keys(movedByOb).forEach((ob) => {
        const c = movedByOb[ob]
        const movedDate = this.normDate(c.date)
        let existing = null
        if (typeof ob === 'string' && ob.indexOf('MOVED-') === 0) {
          const recId = parseInt(ob.slice('MOVED-'.length))
          existing = this.movedShipments.find((m) => m.id === recId)
        } else {
          existing = this.movedShipments.find((m) => !m.is_new && m.ob === ob)
        }
        // `originDate` is the shipment's date in SAP, untouched by any move. Saving
        // it anchors the prediction: the forecast drops the move once SAP moves the
        // shipment somewhere else. A prediction has no SAP date to anchor to.
        // Sliced, not normDate'd: it is already a bare YYYY-MM-DD from the API and
        // the anchor has to match the stored string exactly — a Date round-trip can
        // shift the day and would drop every move on load.
        const originDate = (existing && existing.is_new) || !c.originDate ? null : String(c.originDate).slice(0, 10)
        if (existing) {
          editPayloads.push({
            id: existing.id,
            shipment_type: c.shipType,
            ship_from: c.ship_from,
            ship_to: c.ship_to,
            crate_type: c.type,
            crate_status: c.status,
            moved_date: movedDate,
            origin_date: originDate,
            num_crates: existing.num_crates
          })
        } else {
          createPayloads.push({
            id_site: this.storedPlantId,
            is_new: false,
            ob: ob,
            shipment_type: c.shipType,
            ship_from: c.ship_from,
            ship_to: c.ship_to,
            crate_type: c.type,
            crate_status: c.status,
            moved_date: movedDate,
            origin_date: originDate
          })
        }
      })
      let anySaved = false
      try {
        this.$buefy.toast.open({ message: 'Saving shipment changes', duration: 8000, type: 'is-success' })
        if (createPayloads.length > 0) {
          await Forecast.createMovedShipments(createPayloads)
          anySaved = true
        }
        for (const payload of editPayloads) {
          await Forecast.editMovedShipment(payload)
          anySaved = true
        }
      } catch (error) {
        this.$buefy.toast.open({ message: 'Error saving changes: ' + error.message, duration: 10000, type: 'is-danger' })
      }
      // Re-sync with the server whenever anything persisted, so a retry can't
      // re-send already-saved creates (create is not idempotent — edits are, being
      // keyed by id). If nothing saved (e.g. the create call itself failed), keep
      // the pending changes so the user can simply retry them.
      if (anySaved) {
        this.resetData()
        await this.getData()
      }
      this.isSubmittingMoves = false
    },
    async getData () {
      sessionStorage.setItem('consultingPlant', JSON.stringify(this.selectedPlant))
      sessionStorage.setItem('dateStart', JSON.stringify(this.dateStart))
      sessionStorage.setItem('dateEnd', JSON.stringify(this.dateEnd))
      this.isLoading = true
      this.forecasts = []
      this.crateForecasts = []
      this.crateMeans = []
      this.collMeans = []
      this.falseShipments = []
      this.crateIOs = []
      this.forkliftDiscrepancies = {}
      this.bookings = []
      this.target = []
      this.inventories = []
      this.washingDays = []
      this.storedPlantId = this.selectedPlant.id
      this.storedName = this.selectedPlant.name
      this.loadedMeanFlags = this.meanFlagSignature
      // Whatever the site was actually loaded with is the set worth coming back to,
      // even if the boxes were ticked before the site was picked.
      saveMeanFlags(this.selectedPlant.id, this.meanFlags)
      let commentData = null
      this.$buefy.toast.open({ message: 'Loading Data', duration: 10000, type: 'is-success' })
      try {
        const data = await Forecast.get({
          start: this.dateStart.toLocaleDateString('en-CA'),
          end: this.dateEnd.toLocaleDateString('en-CA'),
          siteId: this.selectedPlant.id,
          collectMean: this.collectMeanCounts,
          washingMean: this.washingMeanCounts,
          repairMean: this.repairMeanCounts,
          rejectMean: this.rejectMeanCounts,
          deliveryMean: this.deliveryMeanCounts,
          ...(this.selectedPlant.id === 'FRA0' && { weeklyMeanSimorep: this.weeklyMeanSimorep })
        })
        commentData = await Forecast.getComments({
          start: this.dateStart.toLocaleDateString('en-CA'),
          end: this.dateEnd.toLocaleDateString('en-CA'),
          siteId: this.selectedPlant.id
        })
        this.holidays = await Forecast.getHolidays({
          siteId: this.selectedPlant.id
        })
        this.falseShipments = data.falseShipments
        this.movedShipments = data.movedShipments || []
        // Working copy of the (already move-applied) collects. `_baseDate` is the
        // effective date at load so we can detect/undo optimistic moves; `_new`
        // flags predictions staged this session that don't exist server-side yet.
        this.collects = (data.collects || []).map((c, i) => ({
          ...c,
          _key: 'c' + i,
          _baseDate: c.date,
          _new: false,
          _pending: false
        }))
        // The forecast archives a move whose SAP shipment has since been rescheduled,
        // and reports it here once. Say so, or the move silently disappears from the
        // board. Counted per OB, since one shipment can carry several archived rows.
        // Read after this.collects is set: movedOb looks the OB up in it.
        const undoneObs = [...new Set((data.undoneMovedShipments || []).map(this.movedOb))]
        if (undoneObs.length > 0) {
          const shown = undoneObs.slice(0, 6).join(', ')
          const rest = undoneObs.length - 6
          this.$buefy.toast.open({
            message: 'Predicted move' + (undoneObs.length > 1 ? 's' : '') + ' dropped — SAP changed the date of OB ' + shown + (rest > 0 ? ' and ' + rest + ' more' : ''),
            duration: 12000,
            type: 'is-warning'
          })
        }
        this.holidays = this.holidays.map((x) => { return new Date(x.date).toLocaleDateString('en-CA') })
        this.inventories = data.inventories && data.inventories.length > 0 ? data.inventories : []
        this.washingDays = data.washingDays && data.washingDays.length > 0 ? data.washingDays : [1, 2, 3, 4, 5]
        // Only the rejected declarations matter here — a trusted one is just the
        // number the forecast already used.
        const discrepancies = {}
        ;(data.forkliftOverrides || []).forEach((o) => {
          if (o.discrepancy) discrepancies[o.date + '__' + o.direction] = o
        })
        this.forkliftDiscrepancies = discrepancies
        // The API merges the site's client types into targetStock.types before
        // returning it, so the columns below already include them; this list is what
        // tells the page which of those columns behave differently.
        this.clientCrateTypes = data.clientCrateTypes || []
        this.target = (data.targetStock && data.targetStock.length > 0) ? {
          id: data.targetStock[0].id,
          id_site: this.selectedPlant.id,
          max_dirty: data.targetStock[0].max_dirty,
          min_dirty: data.targetStock[0].min_dirty,
          max_clean: data.targetStock[0].max_clean,
          min_clean: data.targetStock[0].min_clean,
          max_total: data.targetStock[0].max_total,
          min_repair: data.targetStock[0].min_repair,
          min_available: data.targetStock[0].min_available,
          types: JSON.parse(data.targetStock[0].types)
        } : {
          id_site: this.selectedPlant.id,
          max_dirty: 0,
          min_dirty: 0,
          max_clean: 0,
          min_clean: 0,
          max_total: 0,
          min_repair: 0,
          min_available: 0,
          types: [{ name: '5.2' }]
        }
        this.siteTypes = this.target.types
        this.selectedTypes = this.visibleSiteTypes()
        const simData = await Forecast.getSimulation({
          start: this.dateStart.toLocaleDateString('en-CA'),
          end: this.dateEnd.toLocaleDateString('en-CA'),
          siteId: this.selectedPlant.id,
          types: this.siteTypes.map(x => x.name)
        })
        this.rawSimData = simData
        this.simData = simData
        // The API names the day it cut means and sims off at. The loaded days only see
        // the window and the site's types, so they miss a later declaration, or one of a
        // type not shown (a maintenance row), that the stock still counts; they are only
        // the fallback for an API that does not send it yet.
        const simLimit = data.lastProd
          ? new Date(data.lastProd)
          : new Date(Math.max.apply(null, data.movements.map((e) => {
            if (e.movements.length > 0) {
              return new Date(e.date)
            }
            return new Date('2019-11-11')
          })))
        this.lastDate = simLimit.toLocaleDateString('en-CA')
        simLimit.setDate(simLimit.getDate() + 1)
        simData.forEach((element) => {
          if (new Date(element.date).toLocaleDateString('en-CA') >= simLimit.toLocaleDateString('en-CA')) {
            if (this.siteTypes.map(x => x.name).includes('5.X') && element.type === '5.2') {
              this.simulations[new Date(element.date).toLocaleDateString('en-CA') + '__' + element.operation + '__' + '5.X'] = element.num_crates
            }
            this.simulations[new Date(element.date).toLocaleDateString('en-CA') + '__' + element.operation + '__' + element.type] = element.num_crates
          }
        })
        if (this.siteTypes.map(x => x.name).includes('5.X')) {
          this.collMeans = data.meanColl
          this.collMeans['5.X'] = this.collMeans['5.2'] + this.collMeans['5.5']
        } else {
          this.collMeans = data.meanColl
        }
        data.forecasts.forEach((element) => {
          var dayMoves = data.movements.filter((x) => { return (new Date(x.date).toLocaleDateString('en-CA') === new Date(element.date).toLocaleDateString('en-CA') && x.movements.length > 0) })
          this.crateForecasts.push({
            ...element,
            // The API indexes each movement under every type it touches, so a
            // cross-type one -- preparing a client crate moves 5.5 dirty into
            // TSRC clean -- arrives in two of the per-type buckets being merged
            // here. Deduping by id keeps it from being counted twice in the Washed
            // row and from being listed twice in the day detail.
            movements: dayMoves.length > 0 ? [...new Map(dayMoves.reduce((combined, current) => {
              return combined.concat(current.movements)
            }, []).map(m => [m.id, m])).values()] : null
          })
        })
        const has5X = this.siteTypes.map(x => x.name).includes('5.X')
        data.means.forEach((element) => {
          // Reject means carry `from`/`to` statuses and share a single operation
          // ('reject'); they must be keyed by the transition or every reject pair
          // would collapse onto one key and lose from/to. Other means keep the
          // legacy `type__operation` key. 5.2 + 5.5 fold into 5.X and accumulate.
          const foldType = has5X && (element.type === '5.2' || element.type === '5.5') ? '5.X' : element.type
          const key = element.from && element.to
            ? foldType + '__reject__' + element.from + '__' + element.to
            : foldType + '__' + element.operation
          this.crateMeans[key] = (this.crateMeans[key] || 0) + element.mean
        })

        var collTypes = this.target.types.map(x => x.name)
        this.undecidedCollsIn = await data.collects.filter(x => {
          return (collTypes.includes(x.type) || (collTypes.includes('5.X') && (x.type === '5.2' || x.type === '5.5'))) && ((x.shipment === null && x.done !== 'T') || (new Date(x.done_date).toLocaleDateString('en-CA') === new Date('09-09-2018').toLocaleDateString('en-CA') || (x.date === null && x.done_date === null))) && !(x.shipType === 'TRFO' || x.shipType === 'DEL')
        })
        this.undecidedCollsIn.sort((a, b) => new Date(b.originDate) - new Date(a.originDate))
        if (this.undecidedCollsIn.length > 0) {
          this.totalUndecidedIn = this.undecidedCollsIn.reduce((sum, element) => sum + parseInt(element.num_crates), 0)
        }
        this.undecidedCollsOut = await data.collects.filter(x => {
          return (collTypes.includes(x.type) || (collTypes.includes('5.X') && (x.type === '5.2' || x.type === '5.5'))) && ((new Date(x.done_date).toLocaleDateString('en-CA') === new Date('09-09-2018').toLocaleDateString('en-CA') || (x.date === null && x.done_date === null))) && (x.shipType === 'TRFO' || x.shipType === 'DEL')
        })
        this.undecidedCollsOut.sort((a, b) => new Date(b.originDate) - new Date(a.originDate))
        if (this.undecidedCollsOut.length > 0) {
          this.totalUndecidedOut = this.undecidedCollsOut.reduce((sum, element) => sum + parseInt(element.num_crates), 0)
        }
        this.buildCrateIOs()
      } catch (error) {
        this.$buefy.toast.open({ message: 'Error loading data:' + error.message, duration: 10000, type: 'is-warning' })
      }
      this.crateForecasts.forEach(element => {
        let types = [...new Set(element.forecasts.map(x => {
          return x.status.split('__')[0]
        }))]
        if (collTypes.includes('5.X')) {
          types.splice(types.indexOf('5.2'), 1)
          types.splice(types.indexOf('5.5'), 1)
          types.push('5.X')
        }
        let statuses = [...new Set(element.forecasts.map(x => {
          return x.status.split('__')[1]
        }))].sort((a, b) => this.statusRank(a) - this.statusRank(b))
        let values = []
        let moves = element.movements
        let washed = []
        let repaired = []
        // Rejections collated by (start → end) status. Only the pairs that move
        // crates between the tracked CLEAN/DIRTY/REPAIR stocks are kept; DIRTY→DIRTY
        // rejects have no net stock effect and are omitted. Matched on the parsed
        // start/end status (robust to reject operation naming variations) rather
        // than on the operation string. Null on days with no movements so the
        // export can fall back to the reject-mean prediction (see exportToExcel).
        let rejCleanDirty = []
        let rejCleanRepair = []
        let rejDirtyRepair = []
        let total = 0
        let dirty = 0
        let clean = 0
        let repair = 0
        let available = 0
        let inventory = false
        let comments = commentData.filter(x => new Date(x.date).toLocaleDateString('en-CA') === new Date(element.date).toLocaleDateString('en-CA'))
        types.forEach(type => {
          values[type] = []
          // A client-specific type is prepared out of its ROOT type's dirty stock, so
          // its production movement starts on the root and only ENDS on this type --
          // the opposite end from an ordinary wash. Its preparation figure is shown in
          // the Washed row; nothing is ever repaired for it, so Repaired stays 0.
          //
          // The root's own Washed row is unaffected: it filters on the washing /
          // machineWash operations, which the preparation movement is not, so the same
          // crates are never counted in both columns.
          if (this.isClientType(type)) {
            washed[type] = !moves
              ? null
              : moves.filter((x) => {
                return x.operation === SPECIAL_CLIENT_WASH && x.status_crate_end.split('__')[0] === type
              }).reduce((sum, move) => sum + move.num_crates, 0)
            // Declassification is the reverse flow out of this stock, and the client
            // type is the START of that movement — so unlike preparation it is keyed
            // on status_crate_start. It occupies the row an ordinary type uses for
            // repairs, which a prepared crate never needs.
            repaired[type] = !moves
              ? null
              : moves.filter((x) => {
                return x.operation === SPECIAL_CLIENT_DECLASSIFY && x.status_crate_start.split('__')[0] === type
              }).reduce((sum, move) => sum + move.num_crates, 0)
          } else {
            washed[type] = !moves ? null : moves.filter((x) => {
              return (x.operation === 'washing' || x.operation === 'machineWash') && (x.status_crate_start.split('__')[0] === type || (type === '5.X' && (x.status_crate_start.split('__')[0] === '5.2' || x.status_crate_start.split('__')[0] === '5.5')))
            }).reduce((sum, move) => sum + move.num_crates, 0)
            repaired[type] = !moves ? null : moves.filter((x) => {
              return x.operation === 'repair' && (x.status_crate_start.split('__')[0] === type || (type === '5.X' && (x.status_crate_start.split('__')[0] === '5.2' || x.status_crate_start.split('__')[0] === '5.5')))
            }).reduce((sum, move) => sum + move.num_crates, 0)
          }
          // Sums historical reject movements matching this type and a given
          // start→end status transition. `type === '5.X'` folds 5.2 + 5.5 together
          // to mirror how washed/repaired aggregate.
          const rejectSum = (startStatus, endStatus) => moves.filter((x) => {
            return x.operation && x.operation.toLowerCase().includes('reject') &&
              x.status_crate_start?.split('__')[1] === startStatus &&
              x.status_crate_end?.split('__')[1] === endStatus &&
              (x.status_crate_start.split('__')[0] === type || (type === '5.X' && (x.status_crate_start.split('__')[0] === '5.2' || x.status_crate_start.split('__')[0] === '5.5')))
          }).reduce((sum, move) => sum + move.num_crates, 0)
          rejCleanDirty[type] = !moves ? null : rejectSum('CLEAN', 'DIRTY')
          rejCleanRepair[type] = !moves ? null : rejectSum('CLEAN', 'REPAIR')
          rejDirtyRepair[type] = !moves ? null : rejectSum('DIRTY', 'REPAIR')
          statuses.forEach(status => {
            let buffer = []
            if (type === '5.X') {
              buffer = element.forecasts.filter((x) => {
                return x.status === '5.2' + '__' + status || x.status === '5.5' + '__' + status
              })
              buffer = {
                changed: buffer[0].changed && buffer[1].changed,
                collChanged: buffer[0].collChanged && buffer[1].collChanged,
                num_crates: buffer[0].num_crates + buffer[1].num_crates,
                status: '5.X' + '__' + status
              }
            } else {
              buffer = element.forecasts.filter((x) => {
                return x.status === type + '__' + status
              })[0]
            }
            values[type][status] = {
              num_crates: buffer.num_crates,
              changed: buffer.changed
            }
            if (this.inventories.filter((x) => {
              return new Date(x.date).toLocaleDateString('en-CA') === new Date(element.date).toLocaleDateString('en-CA')
            }).length > 0) {
              inventory = true
            }
            if (buffer.num_crates > 0) {
              total += buffer.num_crates
              if (status === 'CLEAN' || status === 'DIRTY') {
                available += buffer.num_crates
              }
              if (status === 'CLEAN') {
                clean += buffer.num_crates
              } else if (status === 'DIRTY') {
                dirty += buffer.num_crates
              } else if (status === 'REPAIR') {
                repair += buffer.num_crates
              }
            }
          })
        })
        this.forecasts.push({
          date: new Date(element.date).toLocaleDateString('en-CA'),
          types: types,
          statuses: statuses,
          values: values,
          washed: washed,
          repaired: repaired,
          rejCleanDirty: rejCleanDirty,
          rejCleanRepair: rejCleanRepair,
          rejDirtyRepair: rejDirtyRepair,
          total: total,
          dirty: dirty,
          clean: clean,
          repair: repair,
          available: available,
          inventory: inventory,
          comment: comments && comments.length > 0 ? comments[0] : null,
          dayMoves: this.computeDayMoves(new Date(element.date).toLocaleDateString('en-CA'))
        })
      })
      this.isLoading = false
    }
  }
}

</script>

<style scoped>
/* Pinned so the controls and the stock limits stay reachable through a forecast that
   can be months long. This only sticks because #site-wrapper clips on one axis
   instead of hiding overflow - see Layout.vue. */
.forecast-toolbar {
  position: sticky;
  top: 0;
  z-index: 30;
  margin: 0 -1.5rem;
  padding: 0.5rem 1.5rem 0.25rem;
  background-color: #f9fafb;
  border-bottom: 1px solid rgba(0, 0, 0, 0.0625);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

/* A grouped b-field renders root .field > .field-body > .field.is-grouped, and the
   inner one is the flex container, so the wrapping has to be set on a descendant -
   hence the deep selector. Doing it here rather than with Buefy's `group-multiline`
   avoids that class's two side effects: it zeroes the row's own bottom margin, which
   is what separates the rows from each other, and it only spaces `.control` children
   while these rows hold nested fields. */
.toolbar-row >>> .field.is-grouped {
  flex-wrap: wrap;
  row-gap: 0.75rem;
}

.forecast-toolbar.is-condensed {
  padding-top: 0.25rem;
}

.toolbar-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.toolbar-summary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.1rem 0.9rem;
  font-size: 0.85rem;
  padding: 0.15rem 0;
}

.toolbar-summary-plant {
  font-weight: 700;
}

/* Buefy leaves a block-level margin under these, which would break the summary line.
   Both are component roots, so the scope id lands on them. */
.toolbar-summary .switch,
.toolbar-summary .button {
  margin-bottom: 0;
}

.toolbar-toggle {
  flex-shrink: 0;
  margin-left: auto;
}

.shipping-detail {
  display: block;
  -ms-flex-preferred-size: 0;
  flex-basis: 0;
  -webkit-box-flex: 1;
  -ms-flex-positive: 1;
  flex-grow: 1;
  -ms-flex-negative: 1;
  flex-shrink: 1;
  padding: 0.10rem;
}
</style>
